/**
 * VIC Tender Calculation Service
 *
 * This module provides core calculation functions for the VIC tender calculation engine.
 * It handles energy cost, environmental charges, network charges, and complete offer cost calculations.
 *
 * @module calculationService
 */

import type {
  UsageData,
  YearRates,
  EnvironmentalCostBreakdown,
  EnviroRates,
  EnvironmentalCharges,
  NetworkCharges,
  NonEnergyCharges,
  ContractCostBreakdown,
  YearlyCost,
  ParsedOfferData,
  OfferCostResult,
} from '@/features/scopingAi/types';
import { resolveEnvSchemeKey, resolveEnvDisplayKey, reconcileEnvRateAgainstCertificate, normalizeEnvRateUnitByMagnitude } from '../lib/envSchemeRegistry';

/**
 * Constants used across all calculations
 */
export const CALCULATION_CONSTANTS = {
  /** Conversion factor from cents to dollars */
  CENTS_TO_DOLLARS: 0.01,

  /** GST rate (10%) - not applied to calculations as all values are Ex GST */
  GST_RATE: 0.10,

  /**
   * Default contract terms in months - used only as fallback
   * IMPORTANT: Now supports ANY contract term (10, 18, 22, 30, etc.)
   * Actual terms come from parsed data or offer.available_terms
   */
  DEFAULT_CONTRACT_TERMS: [12, 24, 36, 48] as const,

  /** Days in a year for daily charge calculations */
  DAYS_PER_YEAR: 365,
} as const;

/**
 * Year 1 day fraction — 364/365.
 *
 * A contract runs from its start date to the day BEFORE the same date a year later
 * (01/11/2026 – 31/10/2027), so Year 1 bills 364 days, not 365. Every stored cost figure
 * is a full 365-day annual amount, so the whole-of-term multiplier subtracts that one day
 * exactly once — Years 2+ are charged in full.
 *
 * Matches the client's formulas workbook cell-for-cell:
 *   F57 = DATEDIF(01/11/2026, 31/10/2027,"D")/365      = 364/365  = 0.99726
 *   F58 = (DATEDIF(01/11/2026, 31/10/2028,"D") - 1)/365 = 729/365 = 1.99726
 *   F59 = 1094/365 = 2.99726        F60 = 1459/365 = 3.99726
 */
export const YEAR_1_DAY_FRACTION = 364 / 365;

/**
 * Year 1 day fraction — 364/365.
 *
 * A contract runs from its start date to the day BEFORE the same date a year later
 * (e.g. 01/07/2026 – 30/06/2027), so Year 1 bills 364 days, not 365. Every downstream
 * figure (energy, environmental, non-energy) is an annual 365-day amount, so Year 1 is
 * scaled by 364/365 and Years 2+ are charged in full. This reproduces the tender model
 * exactly — its year multipliers are 0.99726 / 1.99726 / 2.99726 / 3.99726, i.e. n − 1/365.
 *
 * ⚠️ Previously this computed days-to-the-next-31-March on an assumed April–March energy
 * year, which bore no relation to the contract's actual dates: a 01/07/2026 start produced
 * 273/365 = 0.748, understating Year 1 by 25%. It was also applied to Stepped contracts
 * only, so Smooth contracts got no Year 1 adjustment at all. Both are fixed — the factor is
 * a property of the contract year, not of the rate type.
 *
 * The constant itself now lives in calculationService alongside termWeightFor(), so the
 * engine, this route, the Offer Details step and the proposal renderer all read one
 * definition — they were previously free to disagree, and did.
 */

export function computeYear1DayFraction(contractStartDate: Date | null | undefined): number {
  // Year 1 runs from the contract start to the day BEFORE the same date a year later, so its
  // real length is 364 days — EXCEPT when a 29 February falls inside that window, when it is
  // 365 and the fraction is exactly 1.0.
  //
  // This previously returned the 364/365 constant unconditionally, which is right only for a
  // Year 1 that misses the leap day. JOHNSON VILLAGE runs 01/07/2027 – 30/06/2028, which
  // contains 29/02/2028: the client's own model scales that year by 365/365 = 1.0 and printed
  // ActewAGL at $80,731, while we scaled by 0.99726 and printed $80,510 — every offer, every
  // column, 0.274% light. Measured on prod: 395 offers across 89 tenders have a 12-month term
  // whose date range is not 364 days.
  //
  // Falls back to the constant when no start date is known, so an offer with no contract dates
  // behaves exactly as before.
  if (!(contractStartDate instanceof Date) || isNaN(contractStartDate.getTime())) {
    return YEAR_1_DAY_FRACTION;
  }
  const startMs = Date.UTC(
    contractStartDate.getUTCFullYear(),
    contractStartDate.getUTCMonth(),
    contractStartDate.getUTCDate(),
  );
  // ⚠️ THE GRID IS ANCHORED TO THE FIRST OF THE START MONTH, NOT TO THE START DATE.
  //
  // Year 1 ends on the last day of the month BEFORE the start month, a year on — not on the
  // day before the anniversary. COWES BAKEHOUSE starts 10/04/2026 and the client's workbook
  // runs Year 1 to 31/03/2027, giving DATEDIF = 355 and the weight ladder
  // 0.972602739726027 / 1.97260273972603 / 2.97260273972603 / 3.97260273972603 — all four
  // exact to fifteen places. The anniversary form gives 09/04/2027 and 364 days, which printed
  // every total on that tender high: Momentum's Year 1 came out 18,204 against their 17,754,
  // and the same ~2.5% rode through Years 2, 3 and 4 for every retailer.
  //
  // ⚠️ A START ON THE FIRST IS UNAFFECTED, WHICH IS WHY THIS WAS INVISIBLE.
  //
  // When the contract starts on the 1st the two forms name the same day, so nothing moves for
  // 6,737 of prod's 7,448 term date ranges — including JOHNSON VILLAGE (01/07/2027), the
  // tender this function's leap-year behaviour was verified against, which still resolves to
  // 30/06/2028 and a fraction of exactly 1.0. Only the 711 ranges that begin mid-month change,
  // and those are the ones that were wrong.
  const anniversaryMs = Date.UTC(
    contractStartDate.getUTCFullYear() + 1,
    contractStartDate.getUTCMonth(),
    1,
  );
  // DATEDIF(start, dayBeforeGridEnd).
  const days = Math.round((anniversaryMs - startMs) / 86_400_000) - 1;
  if (!Number.isFinite(days) || days <= 0) return YEAR_1_DAY_FRACTION;
  return days / 365; // the workbook divides by a fixed 365, never by the year's own length
}

/**
 * Whole-of-term multiplier for an ANNUAL figure: term length in years minus the Year-1
 * day fraction. 12mo → 0.99726, 24mo → 1.99726, 36mo → 2.99726, 48mo → 3.99726.
 *
 * The single definition of this factor. It is applied EXACTLY ONCE, when an annual figure
 * is turned into a contract total — never baked into the stored annual values themselves
 * (doing that made the annual Site Cost Analysis table print 364/365 of every offer cost).
 *
 * @param termMonths - Contract term in months (any value, not just multiples of 12)
 * @returns The multiplier, or 0 for a non-positive/invalid term
 */
export function termWeightFor(termMonths: number): number {
  if (!isFinite(termMonths) || termMonths <= 0) return 0;
  return termMonths / 12 - (1 - YEAR_1_DAY_FRACTION);
}

/**
 * The whole-of-term multiplier to use for a costed term: the engine's figure when it supplied
 * one, else the month-count form.
 *
 * THE ONLY WAY a UI or renderer should obtain this factor. The engine computes term_weight from
 * the term's own quoted dates, so for anything the retailer did not sell in whole years it is
 * the only correct value — recomputing from termMonths throws that away and silently under-bills
 * the contract. Every screen that recomputed it disagreed with the tender generated off the very
 * same row.
 *
 * @param breakdown - A costed term, or null/undefined when none has been calculated yet
 * @param termMonths - Fallback term length for breakdowns written before term_weight existed
 */
export function contractWeightOf(
  breakdown: { term_weight?: number | null } | null | undefined,
  termMonths: number,
): number {
  const stored = Number(breakdown?.term_weight);
  return stored > 0 ? stored : termWeightFor(termMonths);
}

/**
 * Get contract terms to calculate from parsed data
 * Returns all terms present in contractTerms object
 *
 * @param parsedData - Parsed offer data with contractTerms
 * @returns Array of contract term months to calculate
 */
export function getAvailableContractTerms(parsedData: ParsedOfferData): number[] {
  if (!parsedData.contractTerms || typeof parsedData.contractTerms !== 'object') {
    return [...CALCULATION_CONSTANTS.DEFAULT_CONTRACT_TERMS];
  }

  // Keys can be plain numbers ("22", "34") OR variant suffixes ("46_stepped", "46_smooth").
  // Extract the numeric month portion from each key (everything before the first "_" or the whole key).
  // Upper bound is a sanity ceiling against garbled parsing (e.g. a corrupted date range
  // computing to thousands of months) — not a real-world contract length limit. It was
  // previously 72 (6 years), which silently dropped genuine long/non-standard terms and
  // reported "reverted to standard terms" with no indication why. Raised to 240 (20 years)
  // so legitimate long-duration contracts survive; anything beyond that is almost certainly
  // a parsing error, not a real offer.
  const MAX_PLAUSIBLE_TERM_MONTHS = 240;
  const termSet = new Set<number>();
  Object.keys(parsedData.contractTerms).forEach((key) => {
    const numericPart = key.split('_')[0];
    const months = parseInt(numericPart, 10);
    if (!isNaN(months) && months > 0 && months <= MAX_PLAUSIBLE_TERM_MONTHS) {
      termSet.add(months);
    } else if (!isNaN(months) && months > 0) {
      console.warn(`[getAvailableContractTerms] Dropped implausible term "${key}" (${months} months > ${MAX_PLAUSIBLE_TERM_MONTHS}-month sanity cap) — likely a parsing error, not a real contract length`);
    }
  });

  const terms = Array.from(termSet);

  // If no valid terms found, fall back to defaults
  if (terms.length === 0) {
    console.warn(`[getAvailableContractTerms] No valid terms found in contractTerms (keys=[${Object.keys(parsedData.contractTerms).join(',')}]) — falling back to standard terms [12,24,36,48]`);
    return [...CALCULATION_CONSTANTS.DEFAULT_CONTRACT_TERMS];
  }

  return terms.sort((a, b) => a - b);
}

/**
 * Savings calculation result for a specific contract term
 */
export type SavingsResult = {
  /** Annual savings compared to current retailer in $ Ex GST */
  annual_savings: number;

  /** Savings as a percentage of current annual cost */
  savings_percent: number;

  /** Total savings over the contract term in $ Ex GST */
  contract_savings: number;
};

/**
 * Calculate energy costs from usage and rates.
 *
 * Formula: (peak_kwh * peak_rate + offpeak_kwh * offpeak_rate + shoulder_kwh * shoulder_rate) * 0.01
 *
 * @param usage - Usage data containing peak, offpeak, and shoulder consumption in kWh
 * @param rates - Energy rates for peak, offpeak, and shoulder periods in c/kWh
 * @returns Total energy cost in dollars (Ex GST)
 *
 * @example
 * ```typescript
 * const usage: UsageData = {
 *   peak_kwh: 180000,
 *   offpeak_kwh: 120000,
 *   shoulder_kwh: 0,
 *   total_kwh: 300000
 * };
 *
 * const rates: YearRates = {
 *   peak: 28.5,      // 28.5 c/kWh
 *   offpeak: 18.2,   // 18.2 c/kWh
 *   shoulder: null   // No shoulder period
 * };
 *
 * const energyCost = calculateEnergyCost(usage, rates);
 * // Result: (180000 * 28.5 + 120000 * 18.2 + 0 * 0) * 0.01 = $73,170.00
 * ```
 */
export function calculateEnergyCost(usage: UsageData, rates: YearRates): number {
  const peakCost = usage.peak_kwh * rates.peak;
  const offpeakCost = usage.offpeak_kwh * rates.offpeak;

  // Handle null shoulder rate - treat as 0 if not applicable
  const shoulderRate = rates.shoulder ?? 0;
  const shoulderCost = usage.shoulder_kwh * shoulderRate;

  // Convert from cents to dollars
  const totalCost = (peakCost + offpeakCost + shoulderCost) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;

  return totalCost;
}

/**
 * Calculate daily supply charges from daily rate.
 *
 * Formula: dailyRateCents * 365 * 0.01 (convert cents to dollars)
 *
 * @param dailyRateCents - Daily supply rate in cents per day
 * @returns Annual daily supply charges in dollars (Ex GST)
 *
 * @example
 * ```typescript
 * const dailyRate = 95; // 95 cents per day
 * const annualCost = calculateDailySupplyCharges(dailyRate);
 * // Result: 95 * 365 * 0.01 = $346.75
 * ```
 */
export function calculateDailySupplyCharges(dailyRateCents: number): number {
  if (!dailyRateCents || dailyRateCents <= 0) {
    return 0;
  }

  return dailyRateCents * CALCULATION_CONSTANTS.DAYS_PER_YEAR * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
}

/**
 * Calculate current retailer annual cost for comparison.
 *
 * Uses the site's energyDetails (current retailer rates) and usage data
 * to calculate what the customer is currently paying.
 *
 * @param usage - Usage data containing peak, offpeak, and shoulder consumption in kWh
 * @param currentRates - Current retailer rates (peak, offpeak, shoulder in c/kWh)
 * @param dailyChargesCents - Current daily supply charges in cents per day
 * @param nonEnergyCharges - Current annual non-energy charges in dollars
 * @returns Total annual cost with current retailer in dollars (Ex GST)
 *
 * @example
 * ```typescript
 * const usage = { peak_kwh: 99929, offpeak_kwh: 139118, shoulder_kwh: 194959, total_kwh: 434006 };
 * const currentRates = { peak: 12.5, offpeak: 8.2, shoulder: 10.0 };
 * const dailyCharges = 95; // cents/day
 * const nonEnergy = 500; // $/year
 *
 * const currentCost = calculateCurrentRetailerCost(usage, currentRates, dailyCharges, nonEnergy);
 * // Returns annual cost with current retailer
 * ```
 */
export function calculateCurrentRetailerCost(
  usage: UsageData,
  currentRates: { peak: number; offpeak: number; shoulder: number | null },
  dailyChargesCents: number = 0,
  nonEnergyCharges: number = 0
): number {
  // Calculate energy cost using current rates
  const energyCost = calculateEnergyCost(usage, {
    peak: currentRates.peak,
    offpeak: currentRates.offpeak,
    shoulder: currentRates.shoulder,
  });

  // Calculate daily supply charges
  const dailySupplyCost = calculateDailySupplyCharges(dailyChargesCents);

  // Total annual cost = energy + daily supply + non-energy
  return energyCost + dailySupplyCost + nonEnergyCharges;
}

/**
 * Calculate savings compared to current retailer.
 *
 * @param currentAnnualCost - Current retailer annual cost in dollars
 * @param newAnnualCost - New offer annual cost in dollars
 * @param contractYears - Contract term in years (1, 2, 3, or 4)
 * @returns Savings breakdown including annual savings, percentage, and contract total
 *
 * @example
 * ```typescript
 * const currentCost = 25000; // $/year with current retailer
 * const newCost = 19728.74;  // $/year with new offer
 *
 * const savings = calculateSavings(currentCost, newCost, 2);
 * // Result: {
 * //   annual_savings: 5271.26,
 * //   savings_percent: 21.09,
 * //   contract_savings: 10542.52
 * // }
 * ```
 */
export function calculateSavings(
  currentAnnualCost: number,
  newAnnualCost: number,
  contractYears: number
): SavingsResult {
  const annualSavings = currentAnnualCost - newAnnualCost;
  const savingsPercent = currentAnnualCost > 0
    ? (annualSavings / currentAnnualCost) * 100
    : 0;
  const contractSavings = annualSavings * contractYears;

  return {
    annual_savings: Math.round(annualSavings * 100) / 100,
    savings_percent: Math.round(savingsPercent * 100) / 100,
    contract_savings: Math.round(contractSavings * 100) / 100,
  };
}

/**
 * Get environmental rates for a specific year.
 * Falls back to year_1 rates if the requested year is not available.
 *
 * @param charges - Environmental charges object with yearly rates
 * @param yearNum - Year number (1-4)
 * @returns Environmental rates for the specified year, or year_1 as fallback
 *
 * @example
 * ```typescript
 * const charges: EnvironmentalCharges = {
 *   year_1: { lrec_rate: 1.85, veec_rate: 0.95, sres_rate: 2.15 },
 *   year_2: { lrec_rate: 1.92, veec_rate: 0.98, sres_rate: 2.23 }
 * };
 *
 * const year2Rates = getEnvironmentalRatesForYear(charges, 2);
 * // Returns: { lrec_rate: 1.92, veec_rate: 0.98, sres_rate: 2.23 }
 *
 * const year3Rates = getEnvironmentalRatesForYear(charges, 3);
 * // Returns: { lrec_rate: 1.85, veec_rate: 0.95, sres_rate: 2.15 } (fallback to year_1)
 * ```
 */
export function getEnvironmentalRatesForYear(
  charges: EnvironmentalCharges,
  yearNum: number
): EnviroRates {
  const yearKey = `year_${yearNum}` as keyof EnvironmentalCharges;
  const yearRates = charges[yearKey];

  // If year-specific rates exist, return them
  if (yearRates) {
    return yearRates;
  }

  // Fallback to year_1 if available
  if (charges.year_1) {
    return charges.year_1;
  }

  // If no rates available, return empty rates object
  return {
    lrec_rate: null,
    veec_rate: null,
    sres_rate: null,
  };
}

/**
 * Contract years within a term that have no energy rate at all — neither peak nor off-peak,
 * from any source.
 *
 * A term-average rate is a plain mean over the term's years, so a year the retailer never
 * quoted contributes a 0 to the numerator while still counting in the denominator. Origin
 * quoted two years (8.5742 / 9.5977 c/kWh) on a tender whose available_terms nonetheless
 * listed 48 and 60 months, and the 48-month table printed 4.2871 c/kWh — exactly
 * (8.5742 + 8.5742 + 0 + 0) / 4 — with an annual energy cost precisely half the 24-month
 * one. The offer was not merely wrong, it sorted FIRST: fabricating a cheaper rate for every
 * year the retailer declined to quote makes an offer look better the longer the term.
 *
 * An offer that quoted nothing for a year cannot be priced over a term containing it, so the
 * term is dropped rather than averaged down. available_terms alone cannot decide this — it is
 * a declaration, not evidence: Next Business Energy listed [12, 24] and was correctly absent
 * from the 48-month table, while Origin listed 48 and 60 with no rates behind either.
 *
 * Rates of 0 and null are both "not quoted" — a 0 c/kWh energy rate is not a real offer.
 *
 * @param yearNumbers - The contract years this term spans, 1-based (e.g. [1,2,3,4] for 48mo)
 * @param sources - Every place a rate for a given year may live, in precedence order
 * @returns The unpriced year numbers, empty when every year has at least one rate
 */
/**
 * Below this, an environmental rate is dollars per kWh; at or above it, cents per kWh.
 *
 * Australian environmental certificate charges land between roughly 0.05 and 2 c/kWh, which
 * is 0.0005 to 0.02 $/kWh — two bands that do not overlap. Prod bears that out exactly:
 * 1,427 stored rates across 520 sites sit at 0.019212 and below, and 71 rates across 27
 * sites sit at 0.0918 and above, with nothing in between. 0.05 falls in that gap.
 */
const ENV_RATE_DOLLARS_PER_KWH_CEILING = 0.05;

/**
 * An environmental rate normalised to c/kWh, whichever unit it was stored in.
 *
 * Site current-contract rates and offer rates disagree about units and always have. On the
 * MAIN TRADING site the current contract stores VEEC as 0.011684 ($/kWh) while the offer
 * beside it stores 0.8533 (c/kWh) — and the same record's ENERGY rates are cents
 * (peak 7.5819), so the shape of the object says nothing about it. Applying the cents
 * conversion to a dollars rate printed the Site Cost Analysis environmental block as
 * VEEC $18.69, LREC $8.48, SREC $7.45 — a $35 total where the client's model reads
 * $3,462.08. Exactly 100x, on the current-contract side only, which understates what the
 * customer pays today and so understates the saving.
 *
 * Decided per rate by magnitude rather than per source, because both units genuinely occur
 * in both places. Same approach as this file's other unit guards (power_percentage < 2 is a
 * fraction; meter_supply_rate >= 200 is an annual total).
 *
 * @param rate - The stored rate, in either unit
 * @returns The rate in c/kWh, ready for the usual CENTS_TO_DOLLARS multiply
 */
export function environmentalRateInCents(rate: number | null | undefined): number {
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate <= 0) return 0;
  return rate < ENV_RATE_DOLLARS_PER_KWH_CEILING ? rate * 100 : rate;
}

/**
 * The contract years a term spans, 1-based. A term that is not a whole number of years adds
 * one more, partial year — a 43-month offer runs into its fourth year and needs a rate for
 * it, so year 4 is included and checked like any other.
 */
export function contractYearNumbers(termMonths: number): number[] {
  const years = (termMonths || 0) / 12;
  if (!isFinite(years) || years <= 0) return [1];
  const whole = Math.floor(years);
  const nums = Array.from({ length: whole }, (_, i) => i + 1);
  if (years - whole > 0) nums.push(whole + 1);
  return nums.length > 0 ? nums : [1];
}

export function unpricedContractYears(
  yearNumbers: number[],
  sources: {
    /** Per-term year rates, e.g. contract_terms['48'].year_3.peak */
    contractTermData?: Record<string, any> | null;
    /** Flat per-year maps from multi_year_rate_data.smooth, e.g. peakRates.year_3 */
    peakRates?: Record<string, any> | null;
    offpeakRates?: Record<string, any> | null;
    /** Year 1's rates, a valid last resort for a Smooth term only (every year is the same). */
    smoothYear1?: Record<string, any> | null;
  },
): number[] {
  const rated = (v: unknown): boolean => {
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return typeof n === 'number' && Number.isFinite(n) && n !== 0;
  };

  return yearNumbers.filter(year => {
    const key = `year_${year}`;
    const termYear = sources.contractTermData?.[key];
    const hasPeak = rated(termYear?.peak) || rated(sources.peakRates?.[key]) || rated(sources.smoothYear1?.peak);
    const hasOffpeak = rated(termYear?.offpeak) || rated(sources.offpeakRates?.[key]) || rated(sources.smoothYear1?.offpeak);
    return !hasPeak && !hasOffpeak;
  });
}

/**
 * Marker set on a normalised environmentalCharges object when its year_N slots are
 * WHOLE-OF-TERM quotes rather than successive contract years. Normalisation throws away
 * `date_range` (it only keeps rates), so the finding has to travel as a flag.
 */
export const TERM_QUOTED_ENV_FLAG = '__term_quoted_env';

/**
 * Do this offer's environmental year_N slots hold one blended rate PER CONTRACT LENGTH,
 * rather than a rate for each successive year?
 *
 * Retailers publish environmental certificate costs in two different ways and the parsed
 * JSON looks identical either way — only `date_range` distinguishes them:
 *
 *   Momentum / Origin — successive years, each slot its own 12 months:
 *     year_1  2027-01-01 → 2027-12-31
 *     year_2  2028-01-01 → 2028-12-31      ← start advances
 *     year_3  2029-01-01 → 2029-12-31
 *
 *   AGL — one blended rate per contract LENGTH, every window from the same start:
 *     year_1  2027-01-01 → 2027-12-31      ← the 1-year deal
 *     year_2  2027-01-01 → 2028-12-31      ← the 2-year deal, already an average
 *     year_3  2027-01-01 → 2029-12-31      ← the 3-year deal
 *
 * Summing AGL's slots averages an average: the 24-month term charged
 * (year_1 + year_2)/2 = $1,733.76/yr against AGL's actual $1,594.20/yr, and the error grew
 * with the term (+8.8% at 24mo, +9.9% at 36mo, +11.3% at 48mo) because each longer slot is
 * cheaper and kept being dragged up by year_1. The client's own formulas workbook encodes
 * exactly this split — its AGL rows read one slot, every other retailer's rows average.
 *
 * Decided from the data, never from the retailer's name: identical start dates across two
 * or more slots means the windows are cumulative. Absent/partial `date_range` returns false,
 * so anything that does not clearly say otherwise keeps the per-year behaviour.
 */
export function detectTermQuotedEnv(rawEnvCharges: unknown): boolean {
  if (!rawEnvCharges || typeof rawEnvCharges !== 'object') return false;
  const root = rawEnvCharges as Record<string, any>;

  if (root[TERM_QUOTED_ENV_FLAG] === true) return true;

  const str = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
  const rangeOf = (slot: any): { start: string; end: string } | null => {
    const start = str(slot?.date_range?.start_date ?? slot?.dateRange?.startDate);
    const end = str(slot?.date_range?.end_date ?? slot?.dateRange?.endDate);
    return start && end ? { start, end } : null;
  };

  // Collect one date range per year slot. Two shapes reach this function: year-first
  // ({ year_1: {...} }) and scheme-first ({ LREC: { year_1: {...} } }). A scheme-first
  // object is authoritative when present — the year-first form is usually the normalised
  // one, which has already dropped date_range.
  const ranges = new Map<string, { start: string; end: string }>();
  const collect = (container: Record<string, any> | undefined) => {
    if (!container) return;
    for (const [key, slot] of Object.entries(container)) {
      if (!key.startsWith('year_')) continue;
      const range = rangeOf(slot);
      if (range && !ranges.has(key)) ranges.set(key, range);
    }
  };

  collect(root);
  for (const value of Object.values(root)) {
    if (value && typeof value === 'object') collect(value as Record<string, any>);
  }

  if (ranges.size < 2) return false;
  const values = [...ranges.values()];

  // Cumulative means both halves: the windows share a start AND they grow. A shared start
  // with a shared end is not a term quote — it is a parser that stamped one date range onto
  // every slot, and collapsing those onto a single slot would be a silent mispricing. Every
  // one of the 44 genuinely term-quoted offers on prod has growing ends; none of the other
  // 208 shares a start at all, so this costs nothing today and closes that failure mode.
  const sameStart = values.every(v => v.start === values[0].start);
  const endsDiffer = new Set(values.map(v => v.end)).size > 1;
  return sameStart && endsDiffer;
}

/**
 * Which environmental year slot a given contract year should read.
 *
 * Term-quoted offers (see detectTermQuotedEnv) collapse to a single slot — the one matching
 * the whole contract length — so every year of the term reads the same already-blended rate.
 * Everything else reads its own year, unchanged.
 *
 * @param envCharges - The offer's environmentalCharges, raw or normalised
 * @param termMonths - Contract length in months
 * @param contractYear - Which year of the contract is being priced (1-based)
 */
export function envYearForTerm(
  envCharges: unknown,
  termMonths: number,
  contractYear: number,
  offerTermMonths?: number[] | null
): number {
  if (!detectTermQuotedEnv(envCharges)) return contractYear;

  // Which slot holds THIS term's blended price.
  //
  // A term-quoted offer publishes one slot per DEAL it offers, in the order it offers them —
  // so the slot is the term's position in the offer's own term list, not its year count.
  // WPG SYDNEY's AGL offer quotes 16/28/40/52 months against four slots holding exactly those
  // four deals' certificate prices (8.8523 / 8.9135 / 8.5735 / 8.3872, references
  // 1057721030-033). Deriving the slot from ceil(termMonths / 12) sent the 16-month term to
  // slot 2 and priced it at the 28-month deal's certificates — $1,934 environmental against
  // the client model's $2,138, on a term whose own slot was sitting right there at slot 1.
  //
  // The year-count form is kept as the fallback for offers whose term list is unknown, and for
  // the whole-year term sets where the two agree anyway (12/24/36/48 -> 1/2/3/4).
  const termIndex = Array.isArray(offerTermMonths) && offerTermMonths.length > 0
    ? [...new Set(offerTermMonths)].sort((a, b) => a - b).indexOf(termMonths) + 1
    : 0;
  const termYears = termIndex > 0
    ? termIndex
    : Math.max(1, Math.ceil((termMonths || 12) / 12));

  // Read the Nth slot the offer ACTUALLY SUPPLIES, not the slot literally numbered N.
  //
  // ⚠️ THE year_N KEYS ARE POSITIONS IN A LIST, NOT YEARS.
  //
  // A term-quoted offer publishes one slot per deal, so the numbering only ever had to be
  // an order. When the parser starts that order at year_2 — as it does whenever the offer
  // omits the partial first period — every deal's slot sits one above where it is looked up.
  //
  // COUNTRY GROCERS (SMYTHESDALE) is the worked example. AGL quotes 21/33/45 months against
  // three slots keyed year_2, year_3, year_4, holding those three deals in that order. The
  // 21-month term is the FIRST deal, so termYears is 1 and the old clamp searched year_1 and
  // below; there is nothing below, so it returned slot 1, no slot answered, and AGL's
  // environmental printed as $0 against the client model's $4,118. Momentum, identical in
  // every respect except that its slots happen to be read per-year, priced correctly — which
  // is what made it look like an AGL data fault rather than this.
  //
  // Reading by position reproduces the client's report exactly on all three terms:
  //   21mo -> 1st slot (year_2)  2,393.56/yr * 1.720548 = 4,118
  //   33mo -> 2nd slot (year_3)  2,406.53/yr * 2.720548 = 6,547
  //   45mo -> 3rd slot (year_4)  2,418.99/yr * 3.720548 = 9,000
  //
  // ⚠️ NOT MATCHED ON THE DATE RANGE, though the slots carry one.
  //
  // AGL's year_2 window reads 12/04/2027 - 31/12/2029, which measures 33 months while the slot
  // holds the 21-month deal. The windows are as unreliable as the keys; the ORDER is the only
  // thing both agree on, and it is the order the client's model reads.
  //
  // Clamping to the last supplied slot keeps the previous behaviour's intent: year_1 is the
  // one-year deal's rate, the most expensive of the set, so a 48-month term quoted only to
  // slot 3 must read slot 3 — the longest deal the offer does quote — not fall back to year_1.
  //
  // Measured on prod: of 113 term-quoted offers, 112 supply year_1..year_N contiguously and
  // take the identical slot under both rules. One — this AGL shape — changes, and that one is
  // the defect. No term-quoted offer has a gap in its slots.
  const supplied = envYearSlotsPresent(envCharges);
  if (supplied.length === 0) return termYears;
  return supplied[Math.min(termYears, supplied.length) - 1];
}

/**
 * Every year_N slot the offer supplies, ascending.
 *
 * Two shapes reach this: year-first ({ year_1: {...} }) and scheme-first
 * ({ LREC: { year_1: {...} } }). A slot counts in either.
 */
function envYearSlotsPresent(envCharges: unknown): number[] {
  if (!envCharges || typeof envCharges !== 'object') return [];
  const root = envCharges as Record<string, any>;
  const slots = new Set<number>();
  const note = (key: string) => {
    const match = /^year_(\d+)$/.exec(key);
    if (match) slots.add(parseInt(match[1], 10));
  };
  for (const [key, value] of Object.entries(root)) {
    if (!value || typeof value !== 'object') continue;
    note(key);                                                    // year-first
    for (const [inner, slot] of Object.entries(value as Record<string, any>)) {
      if (slot != null) note(inner);                              // scheme-first
    }
  }
  return [...slots].sort((a, b) => a - b);
}


/**
 * Environmental schemes billed on OFF-PEAK volume instead of total consumption.
 *
 * ⚠️ INTENTIONALLY EMPTY — every scheme bills on TOTAL consumption.
 *
 * This briefly contained 'lrec'. Two client-supplied artifacts disagree, and the more
 * authoritative one won:
 *
 *   SOLARSIGNS tender report (PDF export, values only)
 *     LREC row multiplied by the OFF-PEAK figure (38,199) while SREC and ESC on the same
 *     table used total (232,071). The same shape appeared on a second tender (92,054),
 *     which is why it initially looked like a deliberate rule.
 *
 *   POWERPLAY "formulas" workbook (actual cell formulas, sent by the client specifically
 *   to eliminate calculation discrepancies) — AUTHORITATIVE:
 *     M3 = ((I3*J3)+(I3*K3)+(I3*L3))*(364/365)   where I3 = TOTAL USAGE = 200,000
 *     J3 = LREC. Total usage is used for LREC exactly as for SREC and VEEC.
 *
 * A formula sheet beats a values-only export: the SOLARSIGNS LREC cell was almost certainly
 * pointing at the wrong column, which is the asymmetry originally flagged (why would LREC
 * alone differ from SREC/ESC in the same block?). Billing LREC on off-peak understated
 * environmental by ~$220/site/year on POWERPLAY.
 *
 * The mechanism below is kept rather than deleted: if the business ever confirms a scheme
 * genuinely bills on off-peak volume, adding its key here is the whole change.
 */
export const OFFPEAK_BILLED_ENV_SCHEMES = new Set<string>();

/**
 * Calculate environmental charges from usage and environmental rates.
 *
 * IMPORTANT: Uses powerPercentage fraction when provided.
 * Each certificate type applies to a FRACTION of total_kwh, not the full total.
 * Example: 99,054 total kWh × 7.32% = 7,247.24 kWh effective usage for VEEC.
 *
 * Formula per certificate:
 *   base_usage      = offPeakUsage for schemes in OFFPEAK_BILLED_ENV_SCHEMES, else totalUsage
 *   effective_usage = base_usage * (powerPercentage / 100)  [if powerPercentage > 0]
 *   OR effective_usage = base_usage                          [if powerPercentage = 0 → fallback]
 *   cost = effective_usage * rate * 0.01
 *
 * @param totalUsage - Total annual energy usage in kWh
 * @param enviroRates - Environmental rates for LREC, VEEC, and SRES
 * @param offPeakUsage - Annual OFF-PEAK kWh. Optional: when omitted (or not positive) every
 *   scheme falls back to totalUsage, preserving the previous behaviour for callers that have
 *   no usage split to hand.
 * @returns Breakdown of environmental costs by certificate type in dollars (Ex GST)
 */
export function calculateEnvironmentalCharges(
  totalUsage: number,
  enviroRates: EnviroRates,
  offPeakUsage?: number
): EnvironmentalCostBreakdown {
  const rates = enviroRates as Record<string, number | null | undefined>;

  // Discover every scheme present via `${key}_rate` fields (lrec/veec/sres/esc/prc/reps/eeis/...).
  // Always include the 3 legacy names so callers reading .lrec/.veec/.sres keep working even
  // when that particular scheme has no data for this offer (cost = 0 in that case).
  const schemeKeys = new Set<string>(['lrec', 'veec', 'sres']);
  Object.keys(rates).forEach(k => {
    const m = /^([a-z0-9]+)_rate$/i.exec(k);
    if (m) schemeKeys.add(m[1].toLowerCase());
  });

  // Use powerPercentage fraction when available (> 0), fallback to full total_kwh.
  // Note: normalizeEnvironmentalChargesForCalculation emits `${scheme}_power_pct` while
  // some paths use the `_power_percentage` suffix — check both names, per scheme.
  const schemeCosts: Record<string, number> = {};
  let total = 0;

  // Off-peak base only when the caller actually supplied a usable split — otherwise fall back
  // to total so callers without a breakdown behave exactly as before.
  const hasOffPeak = typeof offPeakUsage === 'number' && isFinite(offPeakUsage) && offPeakUsage > 0;

  schemeKeys.forEach(scheme => {
    const rate = rates[`${scheme}_rate`] ?? 0;
    const pct = rates[`${scheme}_power_percentage`] ?? rates[`${scheme}_power_pct`] ?? rates.powerPercentage ?? 0;

    // OFFPEAK_BILLED_ENV_SCHEMES is empty by default — every scheme bills on TOTAL usage, per
    // the client's formula workbook. The hook stays so a confirmed off-peak scheme is a one-line
    // change; see the constant's note for why LREC is NOT one of them.
    const baseUsage = hasOffPeak && OFFPEAK_BILLED_ENV_SCHEMES.has(scheme)
      ? (offPeakUsage as number)
      : totalUsage;

    // ratePerKwh already encodes the obligation (price × power% / 1000)
    // so when rate > 0, use the full base usage — do NOT apply power% again
    const usage = rate > 0 ? baseUsage : (pct > 0 ? baseUsage * (pct / 100) : baseUsage);
    const cost = usage * rate * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;

    schemeCosts[scheme] = cost;
    total += cost;
  });

  return {
    lrec: schemeCosts.lrec ?? 0,
    veec: schemeCosts.veec ?? 0,
    sres: schemeCosts.sres ?? 0,
    total,
    schemes: schemeCosts,
  };
}

/**
 * Calculate environmental charges WITH per-type powerPercentage from the offer's
 * EnvironmentalCharges structure (LREC/SREC/VEEC each have their own powerPercentage).
 *
 * @param totalUsage - Total annual energy usage in kWh
 * @param envCharges - The offer's environmentalCharges object from multi_year_rate_data.smooth
 * @param yearNum - Year number (1-6) for stepped rates
 * @returns Breakdown of environmental costs with effective usage amounts
 */
export function calculateEnvironmentalChargesFromOfferData(
  totalUsage: number,
  envCharges: any,
  yearNum: number = 1
): {
  lrec: number; veec: number; sres: number; total: number;
  lrec_usage: number; veec_usage: number; sres_usage: number;
  /** Per-scheme breakdown for EVERY scheme present (lrec/sres/veec plus esc/reps/eeis/...) — used to render dynamic line items */
  schemes: Record<string, { cost: number; usage: number; rate: number; label: string }>;
} {
  const yearKey = `year_${yearNum}`;
  const fallbackKey = 'year_1';

  const lrecData = envCharges?.LREC?.[yearKey] || envCharges?.LREC?.[fallbackKey] || {};
  const srecData = envCharges?.SREC?.[yearKey] || envCharges?.SREC?.[fallbackKey] || {};
  const veecData = envCharges?.['VEEC/ESS/REES']?.[yearKey] || envCharges?.['VEEC/ESS/REES']?.[fallbackKey] || {};

  function effectiveUsageAndCost(usage: number, data: any): { effectiveUsage: number; cost: number } {
    const rate = data.ratePerKwh ?? 0;
    const price = data.pricePerCertificate ?? 0;
    const pct = data.powerPercentage ?? 0;

    if (price > 0 && pct > 0) {
      // Normalise: parsers sometimes store power% as fraction (0.1667) not percent (16.67).
      const effectivePct = pct < 2 ? pct * 100 : pct;
      const certDerivedRate = (price * effectivePct) / 1000; // correct c/kWh

      if (rate > 0) {
        // Reconcile the document's rate against the definitional cert x pct / 1000 value —
        // catches $/kWh (100x low), $/MWh (10x low) and cert x pct / 100 (10x high).
        // Shared with calculationRoutes so this screen and the generated proposal agree.
        // pct is passed RAW so the echo guard can compare it against the printed rate — see
        // reconcileEnvRateAgainstCertificate. It is the document's own percentage here; this
        // path has no sys_config fallback to confuse it with.
        const effectiveRate = reconcileEnvRateAgainstCertificate(rate, certDerivedRate, pct);
        const cost = usage * effectiveRate * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
        return { effectiveUsage: usage, cost };
      }

      // No ratePerKwh — compute from cert formula
      const cost = usage * certDerivedRate * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
      return { effectiveUsage: usage, cost };
    }

    if (rate > 0) {
      const effectivePct = pct < 2 ? pct * 100 : pct;
      // Detect cert price stored in ratePerKwh field (common LLM parsing error for AGL-style offers).
      // Real Australian env c/kWh rates are ALWAYS < 2 c/kWh. If rate > 1 and power% is available,
      // treat rate as a cert price ($/cert) and derive the correct c/kWh: cert × pct / 1000.
      // e.g. SRES $40/cert stored as rate=40 → derivedRate = 40 × 11.67/1000 = 0.4668 c/kWh ✓
      if (rate > 1 && effectivePct > 0) {
        const certDerivedRate = (rate * effectivePct) / 1000;
        if (certDerivedRate < 2) { // sanity: result must still be a plausible env rate
          const cost = usage * certDerivedRate * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
          return { effectiveUsage: usage, cost };
        }
      }
      // Rate-only scheme (REPS supplies neither a cert price nor a power %), so there is no
      // definitional value to check against — fall back to magnitude. Shared with
      // calculationRoutes so this screen and the generated proposal agree.
      const cost = usage * normalizeEnvRateUnitByMagnitude(rate) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
      return { effectiveUsage: usage, cost };
    }

    return { effectiveUsage: usage, cost: 0 };
  }

  const lrecResult = effectiveUsageAndCost(totalUsage, lrecData);
  const srecResult = effectiveUsageAndCost(totalUsage, srecData);
  const veecResult = effectiveUsageAndCost(totalUsage, veecData);

  const schemes: Record<string, { cost: number; usage: number; rate: number; label: string }> = {
    lrec: { cost: lrecResult.cost, usage: lrecResult.effectiveUsage, rate: lrecData.ratePerKwh ?? 0, label: 'LREC' },
    sres: { cost: srecResult.cost, usage: srecResult.effectiveUsage, rate: srecData.ratePerKwh ?? 0, label: 'SREC' },
    veec: { cost: veecResult.cost, usage: veecResult.effectiveUsage, rate: veecData.ratePerKwh ?? 0, label: 'VEEC' },
  };

  // Include any additional schemes (ESC, PRC, REPS, EEIS, etc.) in total AND as their own line item
  const knownKeys = new Set(['LREC', 'SREC', 'VEEC/ESS/REES']);
  let additionalTotal = 0;
  if (envCharges && typeof envCharges === 'object') {
    Object.entries(envCharges).forEach(([key, schemeData]) => {
      if (knownKeys.has(key)) return;
      // Skip non-scheme entries. A normalised charges object carries a boolean
      // TERM_QUOTED_ENV_FLAG alongside its year slots; without this it would be read as a
      // scheme and emit a zero-cost "__term_quoted_env" line item into the breakdown.
      if (!schemeData || typeof schemeData !== 'object') return;
      const yearData = (schemeData as any)?.[yearKey] || (schemeData as any)?.[fallbackKey] || {};
      const result = effectiveUsageAndCost(totalUsage, yearData);
      additionalTotal += result.cost;
      const canonicalKey = resolveEnvSchemeKey(key) ?? key.toLowerCase();
      schemes[canonicalKey] = { cost: result.cost, usage: result.effectiveUsage, rate: yearData.ratePerKwh ?? 0, label: resolveEnvDisplayKey(key) };
    });
  }

  return {
    lrec: lrecResult.cost,
    veec: veecResult.cost,
    sres: srecResult.cost,
    total: lrecResult.cost + veecResult.cost + srecResult.cost + additionalTotal,
    lrec_usage: lrecResult.effectiveUsage,
    veec_usage: veecResult.effectiveUsage,
    sres_usage: srecResult.effectiveUsage,
    schemes,
  };
}

// ============================================================================
// NEW STRUCTURED CHARGE CALCULATORS
// ============================================================================

/**
 * Calculate network charges in structured form, returning line items.
 *
 * Formulas:
 *   winter_peak  = peak_kwh    * winter_peak_rate    * 0.01
 *   winter_offpk = offpeak_kwh * winter_offpeak_rate * 0.01
 *   capacity     = peak_kw     * capacity_rate        * 0.01  (N/A if peak_kw = 0)
 *   supply       = supply_charge_rate                  * 0.01  (flat annual)
 */
export function calculateNetworkChargesStructured(
  usage: { peak_kwh: number; offpeak_kwh: number; total_kwh: number },
  peakDemandKw: number,
  rates: {
    winter_peak_rate?: number;
    winter_offpeak_rate?: number;
    capacity_rate?: number;
    supply_charge_rate?: number;
  } | null | undefined
): {
  winter_peak: number;
  winter_offpeak: number;
  capacity: number;
  supply: number;
  total: number;
} {
  if (!rates) {
    return { winter_peak: 0, winter_offpeak: 0, capacity: 0, supply: 0, total: 0 };
  }

  const winterPeak = usage.peak_kwh * (rates.winter_peak_rate ?? 0) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
  const winterOffpeak = usage.offpeak_kwh * (rates.winter_offpeak_rate ?? 0) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
  const capacity = peakDemandKw > 0
    ? peakDemandKw * (rates.capacity_rate ?? 0) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS
    : 0;
  const supply = (rates.supply_charge_rate ?? 0) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;

  return {
    winter_peak: winterPeak,
    winter_offpeak: winterOffpeak,
    capacity,
    supply,
    total: winterPeak + winterOffpeak + capacity + supply,
  };
}

/**
 * Calculate other/retailer-specific charges in structured form.
 *
 * Formulas:
 *   meter_supply      = meter_supply_rate   * 365          ($/meter/day → annual $)
 *   service_charge    = service_charge_rate * 12           ($/month → annual $)
 *   aemo_participant  = peak_kw * aemo_participant_rate * 0.01  (c/kW/year, N/A if peak_kw = 0)
 *   aemo_ancillary    = aemo_ancillary_rate * 0.01         (c/site/year flat annual)
 */
export function calculateOtherChargesStructured(
  usage: { peak_kwh: number; offpeak_kwh: number },
  peakDemandKw: number,
  rates: {
    meter_supply_rate?: number;
    service_charge_rate?: number;
    aemo_participant_rate?: number;
    aemo_ancillary_rate?: number;
  } | null | undefined
): {
  meter_supply: number;
  service_charge: number;
  aemo_participant: number;
  aemo_ancillary: number;
  total: number;
} {
  if (!rates) {
    return { meter_supply: 0, service_charge: 0, aemo_participant: 0, aemo_ancillary: 0, total: 0 };
  }

  const meterSupply = (rates.meter_supply_rate ?? 0) * 365;   // $/meter/day → annual
  const serviceCharge = (rates.service_charge_rate ?? 0) * 12; // $/month → annual
  const aemoParticipant = peakDemandKw > 0
    ? peakDemandKw * (rates.aemo_participant_rate ?? 0) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS
    : 0;
  // Fix: AEMO Ancillary is charged per kW of peak demand (same basis as Participant),
  // not a flat $/site/year fee — multiply by peakDemandKw.
  const aemoAncillary = peakDemandKw > 0
    ? peakDemandKw * (rates.aemo_ancillary_rate ?? 0) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS
    : (rates.aemo_ancillary_rate ?? 0) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;

  return {
    meter_supply: meterSupply,
    service_charge: serviceCharge,
    aemo_participant: aemoParticipant,
    aemo_ancillary: aemoAncillary,
    total: meterSupply + serviceCharge + aemoParticipant + aemoAncillary,
  };
}

/**
 * Calculate green premium cost.
 * green_cost = total_kwh * (green_percent / 100) * green_premium_rate * 0.01
 */
export function calculateGreenPremium(
  totalKwh: number,
  greenPercent: number,
  greenPremiumRate: number
): number {
  if (!greenPercent || greenPercent <= 0 || !greenPremiumRate) return 0;
  const greenUsage = totalKwh * (greenPercent / 100);
  return greenUsage * greenPremiumRate * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
}

/**
 * Build the current contract cost breakdown for a single site.
 * Returns category totals + detailed line items for proposal display.
 */
export function buildCurrentContractBreakdown(
  usage: { peak_kwh: number; offpeak_kwh: number; shoulder_kwh: number; total_kwh: number },
  peakDemandKw: number,
  currentRates: {
    energy?: { peak_rate?: number; offpeak_rate?: number; shoulder_rate?: number; daily_supply_rate?: number };
    network?: { winter_peak_rate?: number; winter_offpeak_rate?: number; capacity_rate?: number; supply_charge_rate?: number };
    /** Any `${scheme}_rate` / `${scheme}_power_percentage` pair — veec/lrec/srec/esc plus any other registered scheme (reps/eeis/...) */
    environmental?: Record<string, number | undefined>;
    other?: { meter_supply_rate?: number; service_charge_rate?: number; aemo_participant_rate?: number; aemo_ancillary_rate?: number };
  } | null | undefined
): {
  energy: number; network: number; environmental: number; other: number; total: number;
  line_items: {
    energy: Array<{ name: string; usage: number; unit: string; rate: number; cost: number }>;
    network: Array<{ name: string; usage: number; unit: string; rate: number; cost: number; not_applicable?: boolean }>;
    environmental: Array<{ name: string; usage: number; unit: string; rate: number; cost: number }>;
    other: Array<{ name: string; usage: number; unit: string; rate: number; cost: number; not_applicable?: boolean }>;
  };
} {
  const er = currentRates?.energy || {};
  const nr = currentRates?.network || {};
  const envr = currentRates?.environmental || {};
  const rawOr = currentRates?.other || {};

  // ── Fix: meter_supply_rate format normalisation ──────────────────────────
  // Old tender data stored the ANNUAL total (e.g. $4,159.60/yr) in this field.
  // calculateOtherChargesStructured multiplies by 365 assuming $/day, which
  // inflated the figure 365×.  Heuristic: a realistic daily meter charge is
  // well under $200/day, so any value ≥ 200 is almost certainly an annual total
  // already — divide by 365 to recover the daily rate before the multiply.
  const rawMeterRate = rawOr.meter_supply_rate ?? 0;
  const or = {
    ...rawOr,
    meter_supply_rate: rawMeterRate >= 200 ? rawMeterRate / 365 : rawMeterRate,
  };

  // Energy
  const peakCost = usage.peak_kwh * (er.peak_rate ?? 0) * 0.01;
  const offpeakCost = usage.offpeak_kwh * (er.offpeak_rate ?? 0) * 0.01;
  const shoulderCost = usage.shoulder_kwh * (er.shoulder_rate ?? 0) * 0.01;
  const dailyCost = (er.daily_supply_rate ?? 0) * 365 * 0.01;
  const energyTotal = peakCost + offpeakCost + shoulderCost + dailyCost;

  // Network
  const netResult = calculateNetworkChargesStructured(usage, peakDemandKw, nr);

  // ── Fix: power_percentage fraction normalisation ─────────────────────────
  // Old data sometimes stored power_percentage as a decimal fraction (e.g.
  // 0.007 meaning 0.7%) instead of a true percentage (0.7).  Australian env
  // power percentages are typically 5-25 %, so any value < 2 is treated as a
  // fraction and multiplied by 100.
  const normPct = (v: number | undefined): number => {
    if (!v || v <= 0) return 0;
    return v < 2 ? v * 100 : v;
  };

  // Discover every scheme present via `${key}_rate` fields — always include the 4 legacy
  // site-side names (veec/lrec/srec/esc) plus any others (reps/eeis/...) actually stored.
  const ENV_SCHEME_LABELS: Record<string, string> = { veec: 'VEEC', lrec: 'LREC', srec: 'SREC', esc: 'ESC', reps: 'REPS', eeis: 'EEIS', prc: 'PRC' };
  const currentSchemeKeys = new Set<string>(['veec', 'lrec', 'srec', 'esc']);
  Object.keys(envr).forEach(k => {
    const m = /^([a-z0-9]+)_rate$/i.exec(k);
    if (m) currentSchemeKeys.add(m[1].toLowerCase());
  });

  let envTotal = 0;
  const environmentalLineItems: Array<{ name: string; usage: number; unit: string; rate: number; cost: number }> = [];
  currentSchemeKeys.forEach(scheme => {
    const rate = envr[`${scheme}_rate`] ?? 0;
    const pct = normPct(envr[`${scheme}_power_percentage`]);
    // Every scheme bills on TOTAL usage (OFFPEAK_BILLED_ENV_SCHEMES is empty — see its note).
    // The line item's `usage` column carries the same base, so the printed row reads
    // rate x usage = cost and reconciles.
    const baseUsage = OFFPEAK_BILLED_ENV_SCHEMES.has(scheme) && usage.offpeak_kwh > 0
      ? usage.offpeak_kwh
      : usage.total_kwh;
    const schemeUsage = pct > 0 ? baseUsage * (pct / 100) : baseUsage;
    // Site rates arrive in either $/kWh or c/kWh with nothing to distinguish them but the
    // magnitude — see environmentalRateInCents. The line item keeps the rate AS STORED so the
    // row still reads the way the client's own model prints it (0.011684 x 160,000 = 1,869.44).
    const cost = schemeUsage * environmentalRateInCents(rate) * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
    envTotal += cost;
    environmentalLineItems.push({ name: ENV_SCHEME_LABELS[scheme] || scheme.toUpperCase(), usage: schemeUsage, unit: 'kWh', rate, cost });
  });

  // Other
  const otherResult = calculateOtherChargesStructured(usage, peakDemandKw, or);

  return {
    energy: energyTotal,
    network: netResult.total,
    environmental: envTotal,
    other: otherResult.total,
    total: energyTotal + netResult.total + envTotal + otherResult.total,
    line_items: {
      energy: [
        { name: 'Peak', usage: usage.peak_kwh, unit: 'kWh', rate: er.peak_rate ?? 0, cost: peakCost },
        { name: 'Off Peak', usage: usage.offpeak_kwh, unit: 'kWh', rate: er.offpeak_rate ?? 0, cost: offpeakCost },
        { name: 'Shoulder', usage: usage.shoulder_kwh, unit: 'kWh', rate: er.shoulder_rate ?? 0, cost: shoulderCost },
        { name: 'Daily Supply Charge', usage: 365, unit: '$/site/year', rate: er.daily_supply_rate ?? 0, cost: dailyCost },
      ],
      network: [
        // Generic labels — not VIC-specific (VIC=Winter Peak; NSW/SA/QLD=Peak)
        { name: 'Peak', usage: usage.peak_kwh, unit: 'kWh', rate: nr.winter_peak_rate ?? 0, cost: netResult.winter_peak },
        { name: 'Off Peak', usage: usage.offpeak_kwh, unit: 'kWh', rate: nr.winter_offpeak_rate ?? 0, cost: netResult.winter_offpeak },
        { name: 'Demand', usage: peakDemandKw, unit: 'kW', rate: nr.capacity_rate ?? 0, cost: netResult.capacity, not_applicable: peakDemandKw <= 0 },
        { name: 'Supply Charge', usage: 1, unit: '$/site/year', rate: nr.supply_charge_rate ?? 0, cost: netResult.supply },
      ],
      environmental: environmentalLineItems,
      other: [
        // Display normalised daily rate; cost is correct annual total after *365
        { name: 'Meter Supply Charge', usage: 365, unit: 'days', rate: or.meter_supply_rate ?? 0, cost: otherResult.meter_supply },
        { name: 'Service Charge', usage: 12, unit: 'months', rate: or.service_charge_rate ?? 0, cost: otherResult.service_charge },
        { name: 'AEMO Participant', usage: peakDemandKw, unit: 'kW', rate: or.aemo_participant_rate ?? 0, cost: otherResult.aemo_participant, not_applicable: peakDemandKw <= 0 },
        // Fix: AEMO Ancillary is charged per kW (same as Participant), not a flat $/site/year fee
        { name: 'AEMO Ancillary', usage: peakDemandKw, unit: 'kW', rate: or.aemo_ancillary_rate ?? 0, cost: otherResult.aemo_ancillary, not_applicable: peakDemandKw <= 0 },
      ],
    },
  };
}

/**
 * Build offer cost breakdown for a single site and a single year's rates.
 * Returns category totals + detailed line items for proposal display.
 */
export function buildOfferBreakdown(
  usage: { peak_kwh: number; offpeak_kwh: number; shoulder_kwh: number; total_kwh: number },
  peakDemandKw: number,
  offerRates: {
    peak_rate?: number;
    offpeak_rate?: number;
    shoulder_rate?: number;
    daily_supply_rate?: number;
    network?: { winter_peak_rate?: number; winter_offpeak_rate?: number; capacity_rate?: number; supply_charge_rate?: number };
    environmental?: any; // From EnvironmentalCharges structure (LREC/SREC/VEEC with powerPercentage)
    other?: { meter_supply_rate?: number; service_charge_rate?: number; aemo_participant_rate?: number; aemo_ancillary_rate?: number };
    green_percent?: number;
    green_premium_rate?: number;
  },
  yearNum: number = 1
): {
  energy: number; network: number; environmental: number; other: number; total: number;
  line_items: {
    energy: Array<{ name: string; usage: number; unit: string; rate: number; cost: number }>;
    network: Array<{ name: string; usage: number; unit: string; rate: number; cost: number; not_applicable?: boolean }>;
    environmental: Array<{ name: string; usage: number; unit: string; rate: number; cost: number }>;
    other: Array<{ name: string; usage: number; unit: string; rate: number; cost: number; not_applicable?: boolean }>;
  };
} {
  // Energy
  const peakCost = usage.peak_kwh * (offerRates.peak_rate ?? 0) * 0.01;
  const offpeakCost = usage.offpeak_kwh * (offerRates.offpeak_rate ?? 0) * 0.01;
  const shoulderCost = usage.shoulder_kwh * (offerRates.shoulder_rate ?? 0) * 0.01;
  const dailyCost = (offerRates.daily_supply_rate ?? 0) * 365 * 0.01;
  const greenCost = calculateGreenPremium(usage.total_kwh, offerRates.green_percent ?? 0, offerRates.green_premium_rate ?? 0);
  const energyTotal = peakCost + offpeakCost + shoulderCost + dailyCost + greenCost;

  // Network
  const netResult = calculateNetworkChargesStructured(usage, peakDemandKw, offerRates.network);

  // Environmental — use EnvironmentalCharges structure if available.
  // envSchemes carries EVERY scheme present (lrec/sres/veec plus esc/reps/eeis/...) for dynamic line items.
  let envTotalAll = 0;
  let envSchemes: Record<string, { cost: number; usage: number; rate: number; label: string }> = {};
  if (offerRates.environmental) {
    const envResult = calculateEnvironmentalChargesFromOfferData(usage.total_kwh, offerRates.environmental, yearNum);
    envTotalAll = envResult.total; // includes ESC, PRC, REPS, EEIS, etc.
    envSchemes = envResult.schemes;
  }
  const envTotal = envTotalAll;
  const environmentalLineItems = Object.values(envSchemes).map(s => ({
    name: s.label, usage: s.usage, unit: 'kWh', rate: s.rate, cost: s.cost,
  }));

  // Other
  const otherResult = calculateOtherChargesStructured(usage, peakDemandKw, offerRates.other);

  const energyLineItems: Array<{ name: string; usage: number; unit: string; rate: number; cost: number }> = [
    { name: 'Peak', usage: usage.peak_kwh, unit: 'kWh', rate: offerRates.peak_rate ?? 0, cost: peakCost },
    { name: 'Off Peak', usage: usage.offpeak_kwh, unit: 'kWh', rate: offerRates.offpeak_rate ?? 0, cost: offpeakCost },
    { name: 'Shoulder', usage: usage.shoulder_kwh, unit: 'kWh', rate: offerRates.shoulder_rate ?? 0, cost: shoulderCost },
    { name: 'Daily Supply Charge', usage: 365, unit: '$/site/year', rate: offerRates.daily_supply_rate ?? 0, cost: dailyCost },
  ];
  if (greenCost > 0) {
    energyLineItems.push({ name: 'Green Premium', usage: usage.total_kwh * ((offerRates.green_percent ?? 0) / 100), unit: 'kWh', rate: offerRates.green_premium_rate ?? 0, cost: greenCost });
  }

  return {
    energy: energyTotal,
    network: netResult.total,
    environmental: envTotal,
    other: otherResult.total,
    total: energyTotal + netResult.total + envTotal + otherResult.total,
    line_items: {
      energy: energyLineItems,
      network: [
        { name: 'Peak', usage: usage.peak_kwh, unit: 'kWh', rate: offerRates.network?.winter_peak_rate ?? 0, cost: netResult.winter_peak },
        { name: 'Off Peak', usage: usage.offpeak_kwh, unit: 'kWh', rate: offerRates.network?.winter_offpeak_rate ?? 0, cost: netResult.winter_offpeak },
        { name: 'Demand', usage: peakDemandKw, unit: 'kW', rate: offerRates.network?.capacity_rate ?? 0, cost: netResult.capacity, not_applicable: peakDemandKw <= 0 },
        { name: 'Supply Charge', usage: 1, unit: '$/site/year', rate: offerRates.network?.supply_charge_rate ?? 0, cost: netResult.supply },
      ],
      environmental: environmentalLineItems,
      other: [
        { name: 'Meter Supply Charge', usage: 365, unit: 'days', rate: offerRates.other?.meter_supply_rate ?? 0, cost: otherResult.meter_supply },
        { name: 'Service Charge', usage: 12, unit: 'months', rate: offerRates.other?.service_charge_rate ?? 0, cost: otherResult.service_charge },
        { name: 'AEMO Participant', usage: peakDemandKw, unit: 'kW', rate: offerRates.other?.aemo_participant_rate ?? 0, cost: otherResult.aemo_participant, not_applicable: peakDemandKw <= 0 },
        { name: 'AEMO Ancillary', usage: peakDemandKw, unit: 'kW', rate: offerRates.other?.aemo_ancillary_rate ?? 0, cost: otherResult.aemo_ancillary, not_applicable: peakDemandKw <= 0 },
      ],
    },
  };
}

// ============================================================================
// LEGACY NETWORK CHARGE CALCULATOR (kept for backward compatibility)
// ============================================================================

/**
 * Calculate network charges from total usage and network charge structure.
 *
 * Network charges can be provided in several ways:
 * 1. Pre-calculated total (networkCharges.total exists) - return it directly
 * 2. Usage-based rates (distributionRate + transmissionRate) - calculate from usage
 * 3. Demand and standing charges - calculate annual cost
 * 4. Combination of above
 *
 * @param totalUsage - Total annual energy usage in kWh
 * @param networkCharges - Network charges structure (can be null if not provided)
 * @returns Total annual network charges in dollars (Ex GST)
 *
 * @example
 * ```typescript
 * // Example 1: Pre-calculated total
 * const charges1: NetworkCharges = { total: 8200.00 };
 * const result1 = calculateNetworkCharges(300000, charges1);
 * // Returns: 8200.00
 *
 * // Example 2: Usage-based rates
 * const charges2: NetworkCharges = {
 *   distributionRate: 5.2,    // 5.2 c/kWh
 *   transmissionRate: 3.1     // 3.1 c/kWh
 * };
 * const result2 = calculateNetworkCharges(300000, charges2);
 * // Returns: (300000 * 5.2 + 300000 * 3.1) * 0.01 = 24900.00
 *
 * // Example 3: Standing charges
 * const charges3: NetworkCharges = {
 *   standingCharges: 0.85     // 0.85 $/day
 * };
 * const result3 = calculateNetworkCharges(300000, charges3);
 * // Returns: 0.85 * 365 = 310.25
 *
 * // Example 4: Combination
 * const charges4: NetworkCharges = {
 *   distributionRate: 5.2,
 *   transmissionRate: 3.1,
 *   demandCharges: 8.50,
 *   standingCharges: 0.85
 * };
 * const result4 = calculateNetworkCharges(300000, charges4);
 * // Returns: distribution + transmission + standing = 24900.00 + 310.25 = 25210.25
 * ```
 */
export function calculateNetworkCharges(
  totalUsage: number,
  networkCharges: NetworkCharges | null
): number {
  // If networkCharges is null or undefined, return 0
  if (!networkCharges) {
    return 0;
  }

  // If total is pre-calculated and provided, return it directly
  if (networkCharges.total !== null && networkCharges.total !== undefined) {
    return networkCharges.total;
  }

  // Calculate network charges from components
  let total = 0;

  // Add distribution charges (usage-based, in c/kWh)
  if (networkCharges.distributionRate) {
    total += totalUsage * networkCharges.distributionRate * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
  }

  // Add transmission charges (usage-based, in c/kWh)
  if (networkCharges.transmissionRate) {
    total += totalUsage * networkCharges.transmissionRate * CALCULATION_CONSTANTS.CENTS_TO_DOLLARS;
  }

  // Add demand charges (if applicable, typically $/kW/month)
  // Note: This is a simplified calculation - in reality, demand charges
  // are based on peak demand in kW, not total usage
  if (networkCharges.demandCharges) {
    total += networkCharges.demandCharges;
  }

  // Add standing charges (daily charges, convert to annual)
  if (networkCharges.standingCharges) {
    total += networkCharges.standingCharges * 365;
  }

  return total;
}

/**
 * Calculate non-energy charges from the non-energy charges structure.
 *
 * Non-energy charges are typically fixed annual fees that don't vary with usage.
 * If a total is provided, return it directly. Otherwise, sum up individual components.
 *
 * @param nonEnergyCharges - Non-energy charges structure
 * @returns Total annual non-energy charges in dollars (Ex GST)
 *
 * @example
 * ```typescript
 * // Example 1: Pre-calculated total
 * const charges1: NonEnergyCharges = { total: 425.00 };
 * const result1 = calculateNonEnergyCharges(charges1);
 * // Returns: 425.00
 *
 * // Example 2: Individual components
 * const charges2: NonEnergyCharges = {
 *   serviceCharge: 150.00,
 *   meterAgreement: 120.00,
 *   supplementaryMetering: 85.00,
 *   marketFees: 45.00,
 *   other: 25.00
 * };
 * const result2 = calculateNonEnergyCharges(charges2);
 * // Returns: 150 + 120 + 85 + 45 + 25 = 425.00
 *
 * // Example 3: Partial components
 * const charges3: NonEnergyCharges = {
 *   serviceCharge: 150.00,
 *   meterAgreement: 120.00
 * };
 * const result3 = calculateNonEnergyCharges(charges3);
 * // Returns: 150 + 120 = 270.00
 * ```
 */
export function calculateNonEnergyCharges(nonEnergyCharges: NonEnergyCharges): number {
  // If total is pre-calculated and provided, return it directly
  if (nonEnergyCharges.total !== null && nonEnergyCharges.total !== undefined) {
    return nonEnergyCharges.total;
  }

  // Sum up individual components
  let total = 0;

  if (nonEnergyCharges.serviceCharge) {
    total += nonEnergyCharges.serviceCharge;
  }

  if (nonEnergyCharges.meterAgreement) {
    total += nonEnergyCharges.meterAgreement;
  }

  if (nonEnergyCharges.supplementaryMetering) {
    total += nonEnergyCharges.supplementaryMetering;
  }

  if (nonEnergyCharges.marketFees) {
    total += nonEnergyCharges.marketFees;
  }

  if (nonEnergyCharges.other) {
    total += nonEnergyCharges.other;
  }

  // Sum any custom/unknown keys added manually (display-name keys like "Market Fees", "Admin Fee", etc.)
  const knownKeys = new Set(['serviceCharge', 'meterAgreement', 'supplementaryMetering', 'marketFees', 'other', 'total']);
  for (const [key, val] of Object.entries(nonEnergyCharges)) {
    if (!knownKeys.has(key) && typeof val === 'number' && val > 0) {
      total += val;
    }
  }

  return total;
}

/**
 * Calculate costs for a Smooth rate contract.
 *
 * Smooth contracts have the same rates throughout the entire contract term.
 * This function calculates annual costs and prorates for partial years.
 *
 * UPDATED: Now supports ANY contract term (10, 18, 22, 30 months, etc.)
 * Handles partial years correctly (e.g., 10 months = 0.833 years)
 *
 * @param usage - Usage data containing energy consumption breakdown
 * @param parsedData - Complete parsed offer data structure
 * @param termMonths - Contract term in months (any valid value: 10, 12, 18, 22, 24, 30, 36, etc.)
 * @returns Complete contract cost breakdown with yearly details
 *
 * @example
 * ```typescript
 * const usage: UsageData = {
 *   peak_kwh: 180000,
 *   offpeak_kwh: 120000,
 *   shoulder_kwh: 0,
 *   total_kwh: 300000
 * };
 *
 * const parsedData: ParsedOfferData = {
 *   rateType: 'Smooth',
 *   contractTerms: {
 *     '22': {
 *       year_1: { peak: 28.5, offpeak: 18.2, shoulder: null }
 *     }
 *   },
 *   nonEnergyCharges: { total: 400.00 },
 *   networkCharges: { total: 8200.00 },
 *   environmentalCharges: {
 *     year_1: { lrec_rate: 1.85, veec_rate: 0.95, sres_rate: 2.15 }
 *   }
 * };
 *
 * const breakdown = calculateSmoothContract(usage, parsedData, 22);
 * // Result: Costs for 22 months (1.833 years) with prorated amounts
 * ```
 */
/**
 * NSW business rule: a contract year with no shoulder rate is billed at that year's peak rate.
 *
 * The calculation applies this to every offer on an NSW site (calculationRoutes calls it on all
 * three of its rate-source branches). It lives here so the proposal's Shoulder column can apply
 * the identical rule: on prod 12,244 year slots across 1,584 offers price a peak with no
 * shoulder beside it, and reading those raw would print a rate the term was never billed at —
 * or nothing at all — under a total that was built on the backfilled figure.
 *
 * Returns the term unchanged for non-NSW sites and where a shoulder is genuinely quoted.
 *
 * Open question, inherited and not resolved here: whether "shoulder = peak for NSW" is right
 * for every NSW distributor and tariff, or was a workaround for one historical case. This only
 * keeps display and costing applying the same rule.
 */
export function nswShoulderFallback(
  slot: any,
  isNSW: boolean,
): any {
  if (!isNSW || !slot || typeof slot !== 'object') return slot;
  const shoulder = slot.shoulder;
  if (shoulder !== null && shoulder !== undefined && shoulder !== '' && Number(shoulder) > 0) return slot;
  const peak = Number(slot.peak);
  if (!Number.isFinite(peak) || peak <= 0) return slot;
  return { ...slot, shoulder: peak };
}

/**
 * An offer's overall rate type, worked out from its own term data.
 *
 * Mirrors calculationRoutes.transformOfferToParsedData, which is where the calculation's
 * `parsedData.rateType` comes from. It has to be inferred rather than read off the offer:
 * `offer_cost_breakdown.rate_type` says "Smooth" for every offer on prod, Engie and Origin
 * included, whose terms are stored only under `_stepped` keys and tagged `rate_type: 'Stepped'`
 * — trusting that column would price and print those as smooth.
 *
 * A user's explicit choice wins; otherwise the term entries decide, and an offer carrying both
 * kinds is treated as Smooth, matching the calculation's own default for mixed offers.
 */
export function inferGlobalRateType(
  contractTerms: Record<string, any> | null | undefined,
  selectedRateType?: string | null,
): 'Smooth' | 'Stepped' {
  if (selectedRateType === 'Smooth' || selectedRateType === 'Stepped') return selectedRateType;
  if (!contractTerms || typeof contractTerms !== 'object') return 'Smooth';

  const entries = Object.values(contractTerms);
  const hasStepped = entries.some((t: any) => t?.rate_type === 'Stepped');
  const hasSmooth  = entries.some((t: any) => t?.rate_type === 'Smooth');
  if (hasStepped && !hasSmooth) return 'Stepped';
  if (hasSmooth && !hasStepped) return 'Smooth';
  if (hasStepped && hasSmooth) return 'Smooth';

  // Nothing tagged: fall back to how the keys themselves are named.
  const keys = Object.keys(contractTerms);
  const steppedKeys = keys.some(k => k.endsWith('_stepped'));
  const smoothKeys  = keys.some(k => k.endsWith('_smooth'));
  if (steppedKeys && !smoothKeys) return 'Stepped';
  return 'Smooth';
}

/**
 * The stored key holding a term's data, for callers that want the object rather than the basis.
 *
 * Same order resolveTermRateBasis uses — the three expected spellings first, then the
 * leading-months fallback for a key like `23_smoothed` that matches none of them. Returns the
 * key exactly as stored; nothing is renamed and nothing is written.
 */
export const resolveStoredTermKey = (
  contractTerms: Record<string, any> | null | undefined,
  term: string | number,
): string | null => {
  if (!contractTerms || typeof contractTerms !== 'object') return null;
  // A term is a month count. Without this, String(NaN) asks for `NaN_smooth` — a key prod
  // actually holds, from a parse that failed to read a length — and an unpriceable term would
  // resolve to real-looking rate data.
  const months = parseInt(String(term), 10);
  if (!Number.isFinite(months) || months <= 0) return null;
  const key = String(term);
  if (contractTerms[key]) return key;
  if (contractTerms[`${key}_smooth`]) return `${key}_smooth`;
  if (contractTerms[`${key}_stepped`]) return `${key}_stepped`;
  const { smooth, stepped } = termKeysByLeadingMonths(contractTerms, months);
  return smooth[0] ?? stepped[0] ?? null;
};

/**
 * Which stored term key a given term is priced from, and how.
 *
 * An offer can hold the same term under a bare key ("36"), a "_smooth" variant and a
 * "_stepped" variant at once, and the parser's global rateType is not always right about
 * which is real. This is the single decision both the calculation and the proposal's rate
 * columns go through, so a table can never print a rate from a different basis than the one
 * its dollars were built on.
 *
 * Returns null when the term is not present under any key.
 */
/**
 * The keys an offer ACTUALLY stores a term's months under, when none of the three expected
 * spellings is one of them.
 *
 * ⚠️ AN UNRECOGNISED SUFFIX PRICED THE WHOLE TERM AT ZERO.
 *
 * Every lookup here and in calculationRoutes asks for exactly "23", "23_smooth" or
 * "23_stepped". SmartestEnergy's parse writes `23_smoothed`, which is none of them, so the
 * term resolved to nothing and every figure on it printed $0 — with the rates sitting right
 * there in the row (peak 6.9748, off-peak 5.9414). Dev tender 8cdb0db2 is the report; prod
 * holds 23 SmartestEnergy offers on `24_smoothed` / `36_smoothed`, plus keys like
 * `12_months_15_days` and `31_smooth_2` from other parses.
 *
 * Matched on the LEADING INTEGER, which is the one part of the key every writer agrees on —
 * bulkReterm parses it the same way, and it is what `available_terms` is cleaned down to. The
 * suffix is then read for the word "stepped" and otherwise treated as smooth.
 *
 * ⚠️ FALLBACK ONLY, AND NEVER WHERE IT COULD CHOOSE. The caller tries the three exact keys
 * first, so a well-formed offer never reaches this. `31_smooth_2` and `43_smooth_3` are real
 * prod keys holding a SECOND term of the same length; returning a sorted list and letting the
 * caller take the first keeps that deterministic instead of picking an arbitrary one, and the
 * key is returned as it is stored — nothing is renamed and nothing is written.
 */
const termKeysByLeadingMonths = (
  contractTerms: Record<string, any>,
  termMonths: number,
): { smooth: string[]; stepped: string[] } => {
  const smooth: string[] = [];
  const stepped: string[] = [];
  for (const key of Object.keys(contractTerms).sort()) {
    const months = parseInt(key, 10);
    if (!Number.isFinite(months) || months !== termMonths) continue;
    const value = contractTerms[key];
    if (!value || typeof value !== 'object') continue;
    const isStepped = /stepped/i.test(key) || value.rate_type === 'Stepped';
    (isStepped ? stepped : smooth).push(key);
  }
  return { smooth, stepped };
};

export function resolveTermRateBasis(
  contractTerms: Record<string, any> | null | undefined,
  termMonths: number,
  globalRateType?: string,
): { termKey: string; rateType: 'Smooth' | 'Stepped' } | null {
  if (!contractTerms || typeof contractTerms !== 'object') return null;

  const termKey    = String(termMonths);
  const smoothKey  = `${termKey}_smooth`;
  const steppedKey = `${termKey}_stepped`;

  // Some retailers (NBE, Origin when both types are offered) store Smooth under a bare key
  // like "12" with no "_smooth" suffix at all — only "_stepped" gets a suffix. Checking just
  // `k.endsWith('_smooth')` misses that convention entirely, so also treat a plain key
  // explicitly tagged rate_type: 'Smooth' as evidence real smooth data exists.
  const hasAnySmoothKey = Object.keys(contractTerms).some(k =>
    k.endsWith('_smooth') || (!k.endsWith('_stepped') && contractTerms[k]?.rate_type === 'Smooth')
  );
  const preferSmooth = globalRateType !== 'Stepped' || hasAnySmoothKey;

  const exactKey = preferSmooth
    ? (contractTerms[smoothKey]  ? smoothKey  :
       contractTerms[termKey]    ? termKey    :
       contractTerms[steppedKey] ? steppedKey : null)
    : (contractTerms[steppedKey] ? steppedKey :
       contractTerms[termKey]    ? termKey    :
       contractTerms[smoothKey]  ? smoothKey  : null);

  // Only when the term is under none of the three expected spellings — see the helper.
  const resolvedKey = exactKey ?? (() => {
    const { smooth, stepped } = termKeysByLeadingMonths(contractTerms, termMonths);
    const order = preferSmooth ? [...smooth, ...stepped] : [...stepped, ...smooth];
    return order[0] ?? null;
  })();

  if (!resolvedKey) return null;

  // If we landed on a _stepped key only because no smooth variant existed (fallback), but the
  // offer has smooth variants elsewhere, treat this term as smooth too.
  const keyIsStepped = resolvedKey.endsWith('_stepped');
  const rateType = (keyIsStepped && preferSmooth)
    ? 'Smooth'
    : ((contractTerms[resolvedKey]?.rate_type || globalRateType || 'Smooth') === 'Stepped'
        ? 'Stepped'
        : 'Smooth');

  return { termKey: resolvedKey, rateType };
}

/**
 * The year slot a Smooth term is priced from — one rate for the whole term, not a per-year set.
 *
 * When contract_terms are stored as energy-year splits (a Nov 20 start makes year_1 a Nov→Mar
 * fragment), year_1 holds pre-prorated partial rates at roughly 40% of the real c/kWh figure,
 * and year_2 is the authoritative full-year rate. Detected by year_1 being under 60% of year_2.
 *
 * Exported so the proposal's rate columns print exactly this rate for a Smooth term rather
 * than a per-year figure the cost was never built from.
 */
/** The peak c/kWh on a year slot, or null when the slot does not carry one. */
const slotPeak = (slot: any): number | null => {
  const n = Number(slot?.peak);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** The rate a term itself quotes: its LAST priced year, which is its own price either way. */
const ownQuotedPeak = (contractTerms: Record<string, any>, months: number): number | null => {
  const term = contractTerms[String(months)]
    ?? contractTerms[`${months}_smooth`]
    ?? contractTerms[resolveStoredTermKey(contractTerms, months) ?? ''];
  if (!term || typeof term !== 'object') return null;
  const maxYear = Math.max(1, Math.ceil(months / 12));
  for (let n = maxYear; n >= 1; n--) {
    const peak = slotPeak(term[`year_${n}`]);
    if (peak !== null) return peak;
  }
  return null;
};

/**
 * The slot a Smooth term is really quoted at when its year slots hold a DEAL PRICE LIST rather
 * than contract years — or null when they don't, leaving the normal rule to apply.
 *
 * Smooth means ONE flat rate for the whole term, so a smooth term whose years escalate is
 * already contradictory. Next Business Energy shows why: its rate card lists a price per deal
 * (12mo 11.8555, 24mo 13.7843, 36mo 14.5214) and the parser files those down the year slots of
 * each term, so term "36" reads 11.8555 / 13.7843 / 14.5214. Priced as contract years that
 * gave EXCH PTY LTD a 36-month commodity spend of $89,742 against the client model's $96,914;
 * priced flat at the term's own 14.5214 it comes to $96,908. All three NBE terms agree:
 * 12mo 26,721 (26,721), 24mo 61,303 (61,307), 36mo 96,908 (96,914).
 *
 * ⚠️ The test is the PREFIX RELATION, not "use the last year". Measured on prod, only 189 of
 * 312 varying smooth terms even ascend, so a blanket last-year rule would corrupt the other
 * 123. The relation is self-verifying instead: an earlier slot only counts as a deal price if
 * the offer ITSELF quotes that exact rate as a shorter term's own price. Term "36".year_2 is a
 * price only because term "24" quotes 13.7843. That holds for 281 terms across 149 offers; the
 * remaining 28 prove nothing and are left exactly as they are.
 *
 * Flat smooth terms — the overwhelming majority — never reach the escalation check at all.
 */
export function dealPriceListSlot(
  contractTerm: any,
  contractTerms?: Record<string, any> | null,
  termMonths?: number,
): any | null {
  if (!contractTerm || typeof contractTerm !== 'object') return null;
  if (!contractTerms || typeof contractTerms !== 'object') return null;
  if (contractTerm.rate_type === 'Stepped') return null;

  const months = Number(termMonths);
  if (!Number.isFinite(months) || months <= 12) return null; // a one-year term has no list

  const maxYear = Math.max(1, Math.ceil(months / 12));
  const priced: { year: number; peak: number }[] = [];
  for (let n = 1; n <= maxYear; n++) {
    const peak = slotPeak(contractTerm[`year_${n}`]);
    if (peak !== null) priced.push({ year: n, peak });
  }
  if (priced.length < 2) return null;

  // Flat is already correct — nothing to collapse.
  const escalates = priced.some(p => Math.abs(p.peak - priced[0].peak) > 1e-4);
  if (!escalates) return null;

  // Every earlier slot must be a shorter term's OWN quoted price for this to be a price list.
  const last = priced[priced.length - 1];
  for (const { year, peak } of priced) {
    if (year === last.year) continue;
    const shorter = ownQuotedPeak(contractTerms, year * 12);
    if (shorter === null || Math.abs(shorter - peak) > 1e-4) return null;
  }

  return contractTerm[`year_${last.year}`];
}

export function smoothRateYear(
  contractTerm: any,
  contractTerms?: Record<string, any> | null,
  termMonths?: number,
): any {
  // A deal price list filed down the year slots — the term's own price is its last slot.
  const dealSlot = dealPriceListSlot(contractTerm, contractTerms, termMonths);
  if (dealSlot) return dealSlot;

  const y1 = contractTerm?.year_1;
  const y2 = contractTerm?.year_2;
  const year1IsPartial = y2 && (y2.peak ?? 0) > 0 && (y1?.peak ?? 0) < (y2.peak ?? 0) * 0.6;
  return year1IsPartial ? y2 : y1;
}

export function calculateSmoothContract(
  usage: UsageData,
  parsedData: ParsedOfferData,
  termMonths: number,
  /**
   * Day fraction for Year 1.  Australian energy years run April 1–March 31.
   * When a contract starts mid-year (e.g. April 10) Year 1 is only 355/365 of a
   * full year.  Default = 1.0 (no proration) for backward compatibility.
   */
  year1DayFraction: number = 1.0
): ContractCostBreakdown {
  // Calculate number of years (can be fractional like 1.833 for 22 months)
  const years = termMonths / 12;

  // Calculate number of full years and remaining months
  const fullYears = Math.floor(years);
  const remainingMonths = termMonths % 12;
  const partialYearFraction = remainingMonths / 12;

  // Get the contract term data — check plain key and variant suffixes
  const termKey = termMonths.toString();
  const contractTerm =
    parsedData.contractTerms[termKey] ||
    parsedData.contractTerms[`${termKey}_smooth`] ||
    parsedData.contractTerms[`${termKey}_stepped`] ||
    // Last, so a well-formed offer resolves exactly as it always did: the term is under a key
    // none of the three spellings match — SmartestEnergy's `23_smoothed`. Without this the
    // throw below fired and the term never entered costData, so the offer stored no breakdown
    // at all and simply was not in the comparison.
    parsedData.contractTerms[resolveStoredTermKey(parsedData.contractTerms, termKey) ?? ''];

  if (!contractTerm || !contractTerm.year_1) {
    throw new Error(`Contract term ${termMonths} months not found or missing year_1 rates`
      + ` (offer holds [${Object.keys(parsedData.contractTerms || {}).join(', ')}])`);
  }

  // Full-year rates for the smooth calculation — one rate for the whole term. Shared with the
  // proposal's rate columns via smoothRateYear, so a Smooth row prints this exact rate.
  const energyRates = smoothRateYear(contractTerm, parsedData.contractTerms, termMonths);

  // Calculate annual energy cost
  const annualEnergyCost = calculateEnergyCost(usage, energyRates);

  // Environmental charges vary by compliance (calendar) year even for Smooth energy
  // contracts — retailers escalate the certificate percentage/price each year (e.g. Origin
  // LREC 16.67% in 2026 → 19.17% in 2027). The parsed environmentalCharges.year_N slots hold
  // those per-year rates, so compute env PER contract year (mirroring calculateSteppedContract)
  // rather than applying year_1 to every year. Offers that only supply year_1 fall back via
  // getEnvironmentalRatesForYear → identical result, so no regression for single-year env data.
  // envYearForTerm collapses this to a single slot for offers that quote one blended rate per
  // contract LENGTH (AGL) instead of per year — summing those averages an average.
  const envForYear = (yr: number): number => {
    if (!parsedData.environmentalCharges) return 0;
    const slot = envYearForTerm(parsedData.environmentalCharges, termMonths, yr);
    const envRates = getEnvironmentalRatesForYear(parsedData.environmentalCharges, slot);
    return calculateEnvironmentalCharges(usage.total_kwh, envRates, usage.offpeak_kwh).total;
  };

  // Calculate annual network charges (flat across years for a Smooth contract)
  const annualNetworkCost = calculateNetworkCharges(
    usage.total_kwh,
    parsedData.networkCharges || null
  );

  // Calculate annual non-energy charges
  // Fall back to smooth.nonEnergyCharges — UI edits write there when top-level is absent
  const necData = parsedData.nonEnergyCharges
    || (parsedData as any).smooth?.nonEnergyCharges
    || (parsedData as any).smooth?.non_energy_charges
    || null;
  let annualOthersCost = 0;
  if (necData) {
    annualOthersCost = calculateNonEnergyCharges(necData);
  }

  // Calculate annual daily supply charges
  let annualDailySupplyCost = 0;
  if (parsedData.networkCharges?.standingCharges) {
    annualDailySupplyCost = parsedData.networkCharges.standingCharges * CALCULATION_CONSTANTS.DAYS_PER_YEAR;
  }

  // Build yearly breakdown:
  //   • Energy / network / others / daily supply are flat across the term (Smooth)
  //   • Environmental escalates per contract year (envForYear)
  //   • Year 1 (full 12-month year) is scaled by year1DayFraction
  //     (e.g. 355/365 when contract starts April 10 rather than April 1)
  //   • Years 2+ are always full calendar years
  //   • Partial tail year (non-12-month contracts) keeps its month fraction only —
  //     it is always a "year 2+" period so no additional day proration is applied
  const yearlyBreakdown: YearlyCost[] = [];

  for (let year = 1; year <= fullYears; year++) {
    const dayFrac = (year === 1) ? year1DayFraction : 1.0;
    const yearEnv = envForYear(year);
    const yearTotal = annualEnergyCost + yearEnv + annualDailySupplyCost + annualNetworkCost + annualOthersCost;
    yearlyBreakdown.push({
      year,
      energy: annualEnergyCost * dayFrac,
      environment: yearEnv * dayFrac,
      daily_supply: annualDailySupplyCost * dayFrac,
      network: annualNetworkCost * dayFrac,
      others: annualOthersCost * dayFrac,
      total: yearTotal * dayFrac,
    });
  }

  // Partial tail year (for non-12-month contract terms, e.g. 22 months)
  if (remainingMonths > 0) {
    const partialYear = fullYears + 1;
    const tailEnv = envForYear(partialYear);
    const tailTotal = annualEnergyCost + tailEnv + annualDailySupplyCost + annualNetworkCost + annualOthersCost;
    yearlyBreakdown.push({
      year: partialYear,
      energy: annualEnergyCost * partialYearFraction,
      environment: tailEnv * partialYearFraction,
      daily_supply: annualDailySupplyCost * partialYearFraction,
      network: annualNetworkCost * partialYearFraction,
      others: annualOthersCost * partialYearFraction,
      total: tailTotal * partialYearFraction,
    });
  }

  // Contract total = sum of all (prorated) yearly costs
  const contractTotal = yearlyBreakdown.reduce((sum, y) => sum + y.total, 0);

  return {
    energy: yearlyBreakdown.reduce((sum, y) => sum + y.energy, 0),
    environment: yearlyBreakdown.reduce((sum, y) => sum + y.environment, 0),
    daily_supply: yearlyBreakdown.reduce((sum, y) => sum + y.daily_supply, 0),
    network: yearlyBreakdown.reduce((sum, y) => sum + y.network, 0),
    others: yearlyBreakdown.reduce((sum, y) => sum + y.others, 0),
    total: contractTotal,
    // perYear = average annual spend = contractTotal / years.
    // Captures both the Year-1 day-fraction effect and per-year environmental escalation:
    //   • 12mo no-frac:  singleYearTotal / 1                = annual spend
    //   • 24mo:          (yr1 + yr2 with escalated env) / 2 = true average ✓
    //   • 12mo 355/365:  (singleYearTotal × 0.9726) / 1     = prorated amount ✓
    perYear: contractTotal / years,
    yearlyBreakdown,
  };
}

/**
 * Calculate costs for a Stepped rate contract.
 *
 * Stepped contracts have different rates for each year, typically escalating.
 * This function calculates costs for each year individually and sums them for the contract total.
 *
 * UPDATED: Now supports ANY contract term (10, 18, 22, 30 months, etc.)
 * Handles partial years correctly by prorating the final year's costs.
 *
 * @param usage - Usage data containing energy consumption breakdown
 * @param parsedData - Complete parsed offer data structure
 * @param termMonths - Contract term in months (any valid value: 10, 12, 18, 22, 24, 30, 36, etc.)
 * @returns Complete contract cost breakdown with year-specific details
 *
 * @example
 * ```typescript
 * const usage: UsageData = {
 *   peak_kwh: 180000,
 *   offpeak_kwh: 120000,
 *   shoulder_kwh: 0,
 *   total_kwh: 300000
 * };
 *
 * const parsedData: ParsedOfferData = {
 *   rateType: 'Stepped',
 *   contractTerms: {
 *     '22': {
 *       year_1: { peak: 28.5, offpeak: 18.2, shoulder: null },
 *       year_2: { peak: 29.8, offpeak: 19.0, shoulder: null }
 *     }
 *   },
 *   nonEnergyCharges: { total: 400.00 },
 *   networkCharges: { total: 8200.00 },
 *   environmentalCharges: {
 *     year_1: { lrec_rate: 1.85, veec_rate: 0.95, sres_rate: 2.15 },
 *     year_2: { lrec_rate: 1.92, veec_rate: 0.98, sres_rate: 2.23 }
 *   }
 * };
 *
 * const breakdown = calculateSteppedContract(usage, parsedData, 22);
 * // Result: Costs for 22 months with Year 1 full and Year 2 prorated (10/12)
 * ```
 */
export function calculateSteppedContract(
  usage: UsageData,
  parsedData: ParsedOfferData,
  termMonths: number,
  /**
   * Day fraction for Year 1.  Australian energy years run April 1–March 31.
   * When a contract starts mid-year Year 1 is < 365 days.
   * Default = 1.0 (no proration) for backward compatibility.
   */
  year1DayFraction: number = 1.0
): ContractCostBreakdown {
  // Calculate number of years (can be fractional)
  const years = termMonths / 12;

  // Calculate full years and remaining months for proration
  const fullYears = Math.floor(years);
  const remainingMonths = termMonths % 12;
  const partialYearFraction = remainingMonths / 12;

  // Determine how many year calculations we need
  // e.g., 22 months = 1 full year + 10 months of year 2
  const yearsToCalculate = remainingMonths > 0 ? fullYears + 1 : fullYears;

  // Get the contract term data — check plain key and variant suffixes
  const termKey = termMonths.toString();
  const contractTerm =
    parsedData.contractTerms[termKey] ||
    parsedData.contractTerms[`${termKey}_stepped`] ||
    parsedData.contractTerms[`${termKey}_smooth`] ||
    parsedData.contractTerms[resolveStoredTermKey(parsedData.contractTerms, termKey) ?? ''];

  if (!contractTerm) {
    throw new Error(`Contract term ${termMonths} months not found`
      + ` (offer holds [${Object.keys(parsedData.contractTerms || {}).join(', ')}])`);
  }

  // Initialize totals
  let totalEnergy = 0;
  let totalEnvironment = 0;
  let totalDailySupply = 0;
  let totalNetwork = 0;
  let totalOthers = 0;

  // Calculate annual daily supply charges (same each year typically)
  let annualDailySupplyCost = 0;
  if (parsedData.networkCharges?.standingCharges) {
    annualDailySupplyCost = parsedData.networkCharges.standingCharges * CALCULATION_CONSTANTS.DAYS_PER_YEAR;
  }

  // Resolve non-energy charges — support both top-level and smooth-nested paths
  const necData = parsedData.nonEnergyCharges
    || (parsedData as any).smooth?.nonEnergyCharges
    || (parsedData as any).smooth?.non_energy_charges
    || null;

  // Build yearly breakdown by calculating each year individually
  const yearlyBreakdown: YearlyCost[] = [];

  for (let year = 1; year <= yearsToCalculate; year++) {
    // Determine if this is a partial tail year (last year, not a full 12-month year)
    const isPartialYear = year === yearsToCalculate && remainingMonths > 0;

    // Month-based fraction (1.0 for full years, remainingMonths/12 for partial tail)
    const monthFraction = isPartialYear ? partialYearFraction : 1.0;

    // Day fraction: applied ONLY to Year 1 when it is a FULL year.
    // A partial tail year (e.g. last 10 months of a 22-month contract) is always
    // year 2+ so it only gets the month fraction, not an additional day fraction.
    const dayFrac = (year === 1 && !isPartialYear) ? year1DayFraction : 1.0;

    // Combined fraction for this year
    const yearFraction = monthFraction * dayFrac;

    // Get year-specific rates, fallback to year_1 if not available
    const yearKey = `year_${year}` as 'year_1' | 'year_2' | 'year_3' | 'year_4';
    const yearRates = contractTerm[yearKey] || contractTerm.year_1;

    if (!yearRates) {
      throw new Error(`Year ${year} rates not found and no year_1 fallback available`);
    }

    // Calculate year-specific energy cost (scaled by yearFraction)
    const yearEnergyCost = calculateEnergyCost(usage, yearRates) * yearFraction;

    // Calculate year-specific environmental charges (scaled by yearFraction)
    let yearEnvironmentCost = 0;
    if (parsedData.environmentalCharges) {
      // Same slot resolution as the Smooth path — a term-quoted offer reads one blended rate
      // for every year of the term rather than escalating through the slots.
      const envSlot = envYearForTerm(parsedData.environmentalCharges, termMonths, year);
      const envRates = getEnvironmentalRatesForYear(parsedData.environmentalCharges, envSlot);
      const envCosts = calculateEnvironmentalCharges(usage.total_kwh, envRates, usage.offpeak_kwh);
      yearEnvironmentCost = envCosts.total * yearFraction;
    }

    // Calculate year-specific network charges (scaled by yearFraction)
    const yearNetworkCost = calculateNetworkCharges(
      usage.total_kwh,
      parsedData.networkCharges || null
    ) * yearFraction;

    // Calculate year-specific non-energy charges (scaled by yearFraction)
    let yearOthersCost = 0;
    if (necData) {
      yearOthersCost = calculateNonEnergyCharges(necData) * yearFraction;
    }

    // Daily supply cost (scaled by yearFraction)
    const yearDailySupplyCost = annualDailySupplyCost * yearFraction;

    // Calculate year total (including daily supply)
    const yearTotal = yearEnergyCost + yearEnvironmentCost + yearDailySupplyCost + yearNetworkCost + yearOthersCost;

    // Add to yearlyBreakdown
    yearlyBreakdown.push({
      year,
      energy: yearEnergyCost,
      environment: yearEnvironmentCost,
      daily_supply: yearDailySupplyCost,
      network: yearNetworkCost,
      others: yearOthersCost,
      total: yearTotal,
    });

    // Add to contract totals
    totalEnergy += yearEnergyCost;
    totalEnvironment += yearEnvironmentCost;
    totalDailySupply += yearDailySupplyCost;
    totalNetwork += yearNetworkCost;
    totalOthers += yearOthersCost;
  }

  // Contract total = sum of all (prorated) yearly costs
  const contractTotal = totalEnergy + totalEnvironment + totalDailySupply + totalNetwork + totalOthers;

  // perYear = contractTotal / years
  //   Unified formula — works for all cases with or without day fraction:
  //   • 12mo no-frac:  Y1_full / 1           = annual rate ✓
  //   • 12mo 355/365:  Y1×0.9726 / 1         = prorated amount ✓
  //   • 24mo 355/365:  (Y1×0.9726 + Y2) / 2  = average with prorated Y1 ✓
  const perYear = contractTotal / years;

  return {
    energy: totalEnergy,
    environment: totalEnvironment,
    daily_supply: totalDailySupply,
    network: totalNetwork,
    others: totalOthers,
    total: contractTotal,
    perYear,
    yearlyBreakdown,
  };
}

/**
 * Calculate offer costs for all available contract terms.
 *
 * This is the main orchestrator function that:
 * 1. Determines rate type (Smooth vs Stepped)
 * 2. Calculates costs for ALL available contract terms from parsed data (dynamic, not hardcoded)
 * 3. Collects errors without stopping calculation
 * 4. Returns complete results with success status
 *
 * IMPORTANT: Now supports ANY contract term (10, 18, 22, 30 months, etc.)
 * Terms are extracted from parsedData.contractTerms dynamically.
 *
 * @param usage - Usage data containing energy consumption breakdown
 * @param parsedData - Complete parsed offer data structure
 * @returns Offer cost result with calculations for all terms or errors
 *
 * @example
 * ```typescript
 * const usage: UsageData = {
 *   peak_kwh: 180000,
 *   offpeak_kwh: 120000,
 *   shoulder_kwh: 0,
 *   total_kwh: 300000
 * };
 *
 * const parsedData: ParsedOfferData = {
 *   rateType: 'Smooth',
 *   contractTerms: {
 *     '10': { year_1: { peak: 28.5, offpeak: 18.2, shoulder: null } },
 *     '22': { year_1: { peak: 28.5, offpeak: 18.2, shoulder: null }, year_2: { ... } },
 *     '36': { ... }
 *   },
 *   nonEnergyCharges: { total: 400.00 },
 *   networkCharges: { total: 8200.00 },
 *   environmentalCharges: {
 *     year_1: { lrec_rate: 1.85, veec_rate: 0.95, sres_rate: 2.15 }
 *   }
 * };
 *
 * const result = calculateOfferCosts(usage, parsedData);
 * // Result: {
 * //   success: true,
 * //   costData: {
 * //     '10': { energy: ..., environment: ..., ... },
 * //     '22': { energy: ..., environment: ..., ... },
 * //     '36': { energy: ..., environment: ..., ... }
 * //   },
 * //   errors: [],
 * //   calculatedAt: '2026-01-28T19:40:00.000Z',
 * //   rateType: 'Smooth',
 * //   usageSource: 'parsed'
 * // }
 * ```
 */
export function calculateOfferCosts(
  usage: UsageData,
  parsedData: ParsedOfferData,
  usageSource: 'parsed' | 'manual' | 'override' = 'parsed',
  /**
   * Day fraction for Year 1 of the new contract.
   * Australian energy years run April 1–March 31; when a contract starts mid-year
   * (e.g. April 10) Year 1 has fewer than 365 days.
   * Default = 1.0 (no proration) for backward compatibility.
   */
  year1DayFraction: number = 1.0
): OfferCostResult {
  const termsToCalculate = getAvailableContractTerms(parsedData);
  console.log(`[calc/start] rateType=${parsedData.rateType} | terms=[${termsToCalculate.join(',')}] | peak=${usage.peak_kwh} offpeak=${usage.offpeak_kwh} total=${usage.total_kwh} kWh | dayFrac=${year1DayFraction.toFixed(5)}`);

  const errors: string[] = [];
  const costData: { [termMonths: string]: ContractCostBreakdown } = {};
  const calculatedAt = new Date().toISOString();

  // Global rateType is used as a fallback only.
  // Per-term rate_type (inside each ContractTermData) takes priority.
  const globalRateType = parsedData.rateType;

  // Loop through ALL available contract terms from parsed data (dynamic)
  for (const termMonths of termsToCalculate) {
    const termKey = termMonths.toString();

    // Resolve the actual term data key and this term's effective rate type. Shared with the
    // proposal renderer (see resolveTermRateBasis) so the rate a table prints is the rate this
    // calculation priced the term with.
    const basis = resolveTermRateBasis(parsedData.contractTerms, termMonths, globalRateType);
    const smoothKey  = `${termKey}_smooth`;
    const steppedKey = `${termKey}_stepped`;
    const actualTermKey = basis?.termKey ?? null;

    if (!actualTermKey) {
      // Surface this as a real diagnostic (not just a console.log) — it means
      // getAvailableContractTerms() thought this term existed but no matching key
      // (bare/_smooth/_stepped) was found in contractTerms, so this term is silently
      // missing from the offer's calculated costs rather than a deliberate skip.
      const msg = `${termKey} months: term key not found in contractTerms`
        + ` (checked "${termKey}", "${smoothKey}", "${steppedKey}",`
        + ` and any key starting ${termKey}; offer holds [${Object.keys(parsedData.contractTerms || {}).join(', ')}])`;
      errors.push(msg);
      console.warn(`  [term] ✗ ${termKey}mo | ${msg}`);
      continue;
    }

    // Resolve effective rate type.
    // If we landed on a _stepped key only because no smooth variant existed (fallback),
    // but the offer has smooth variants elsewhere, treat this term as smooth too.
    const termData = parsedData.contractTerms[actualTermKey];
    const effectiveRateType: string = basis!.rateType;
    // Log (don't silently resolve) when calc-time's effective rate type disagrees with what
    // was actually stored on this term at parse time — this disagreement is itself a bug
    // signal (Cluster B): it means either the parse-time classification or this fallback
    // logic is wrong for this offer, and whichever "wins" here may not match what a user
    // reviewing the stored data would expect.
    const storedRateType = (termData as any).rate_type;
    if (storedRateType && storedRateType !== effectiveRateType) {
      console.warn(`  [term] ⚠ ${termKey}mo key=${actualTermKey} | rate_type disagreement: stored="${storedRateType}" vs effective="${effectiveRateType}" (globalRateType="${globalRateType}") — using effective for this calculation`);
    }
    const calculateFunction =
      effectiveRateType === 'Stepped' ? calculateSteppedContract : calculateSmoothContract;

    // Try to calculate costs for this term
    try {
      // The Year 1 fraction is a property of the CONTRACT YEAR (it bills 364 days, not 365),
      // not of the rate type — a Smooth contract's first year is exactly as short as a
      // Stepped one's. Excluding Smooth here overstated every Smooth offer's first year by
      // one day's worth of energy, environmental and non-energy charges.
      const breakdown = calculateFunction(usage, parsedData, termMonths, year1DayFraction);
      costData[termKey] = breakdown;
      console.log(`  [term] ✓ ${termKey}mo key=${actualTermKey} type=${effectiveRateType} | energy=$${breakdown.energy.toFixed(0)} env=$${breakdown.environment.toFixed(0)} net=$${breakdown.network.toFixed(0)} others=$${breakdown.others.toFixed(0)} daily=$${breakdown.daily_supply.toFixed(0)} perYear=$${breakdown.perYear.toFixed(0)}`);
    } catch (error) {
      // Collect error but continue with other terms
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Failed to calculate ${termMonths}-month contract`;
      errors.push(`${termMonths} months: ${errorMessage}`);
      console.warn(`  [term] ✗ ${termKey}mo | ${errorMessage}`);
    }
  }

  // Determine overall success
  const success = Object.keys(costData).length > 0;
  if (!success) console.warn(`[calc/end] ✗ no terms calculated | errors=${errors.join('; ')}`);

  return {
    success,
    costData: success ? costData : null,
    errors,
    calculatedAt,
    rateType: parsedData.rateType,
    usageSource,
  };
}

/**
 * The contract-level money for a term, rebuilt from the per-site ANNUAL rows and the term's own
 * multiplier.
 *
 * ⚠️ THERE ARE TWO WEIGHTING IMPLEMENTATIONS AND THEY DISAGREE.
 *
 * calculationService's calculateSmoothContract / calculateSteppedContract do their own
 * month-based proration internally — `years = termMonths / 12`, then a `remainingMonths / 12`
 * tail — so breakdown.energy for a 16-month term comes back weighted 0.99726 + 0.33333 =
 * 1.330594. Meanwhile term_weight, computed here, is the date- and window-derived 1.364384.
 * Publishing both put three different numbers in one row:
 *
 *     energy   18832.50   (service weight)
 *     total    27258.81   (service weight)
 *     perYear  20351.05   (service's own per-year mean)
 *     sites[]  20368.08   (true annual)  x term_weight 1.364384 = 27789.79
 *
 * The screens and the proposal renderer all multiply sites[] by term_weight, so they showed
 * 27,790 while cost_data.total said 27,259 — the mismatch, and the reason the annual figure
 * never equalled total / years either.
 *
 * sites[] is the trustworthy input: every component there is a full 365-day annual amount built
 * from the offer's own rates. Applying term_weight once to that sum is the only figure that
 * agrees with what is displayed. `others` and `daily_supply` deliberately stay ANNUAL in the
 * term object, as documented on term_weight and as the proposal template expects.
 */
export function termMoneyFromSites(sites: any[], totalWeight: number): {
  energy: number; environment: number; total: number; perYear: number;
} {
  const sum = (field: string): number =>
    (sites || []).reduce((acc: number, site: any) => acc + (Number(site?.[field]) || 0), 0);
  const annualEnergy = sum('energy');
  const annualEnv = sum('environment');
  const annualPerYear = annualEnergy + annualEnv + sum('network') + sum('others') + sum('daily_supply');
  const round2 = (n: number): number => Math.round(n * 100) / 100;
  return {
    energy: round2(annualEnergy * totalWeight),
    environment: round2(annualEnv * totalWeight),
    total: round2(annualPerYear * totalWeight),
    perYear: round2(annualPerYear),
  };
}
